/**
 * client/renderer.ts
 * Responsabilidad: Dibujado de alta fidelidad en Canvas 2D a 60 FPS (fondo, cuadrícula, bordes, comidas, jugadores, animaciones de muerte y efectos elementales).
 *
 * Emplea renderizado procedural sub-pixel con culling visual para optimizar el rendimiento por cuadro.
 */

import { Camera } from "./camera";
import {
    MAP_WIDTH,
    MAP_HEIGHT,
    ELEMENTS,
    DEATH_ANIMATION_MS,
    ELEMENTAL_COUNTERS,
    EAT_RATIO,
    type PowerUpType,
    POWERUP_CONFIG,
    POWERUP_RADIUS,
    METEOR_IMPACT_RADIUS,
    GRAVITY_CORE_RADIUS,
    METEOR_WARNING_MS,
    EMOTE_CATALOG,
} from "../src/shared/constants";
import { emoteManager } from "./emotes";
import type {
    RenderablePlayer,
    DeathAnimation,
    ElementalHitEffect,
    TrailPoint,
    FoodParticle,
    RespawnRing,
    PowerUpRenderable,
    PowerUpPickupEffect,
    ClientEvent,
    MeteorRender,
    MeteorImpactEffect,
    GravityCaptureEffect,
    ProximityParticle,
    ProximityReactionKind,
    FloatingCombatText
} from "./types";
import {
    drawPlayerSkin,
    drawSkinOverlay,
    drawModularSkin,
    type SkinId,
    type PlayerCosmeticsConfig,
    DEFAULT_COSMETICS
} from "./skins";
import { hashString } from "./utils";
import { t } from "./i18n";
import { FamiliarParticleSystem, getFamiliarTargetPosition, getFamiliarElement } from "./familiars";
import { getDefaultSkinForElement } from "./element-skins";
import { drawCanvasElementSkin } from "./element-skins-renderer";

/** Caché de imágenes HTMLImageElement para los 15 assets de familiares */
const familiarImageCache = new Map<string, HTMLImageElement>();
export function getFamiliarImage(id: string): HTMLImageElement | null {
    if (!id || id === "none") return null;
    let img = familiarImageCache.get(id);
    if (!img) {
        img = new Image();
        img.src = `assets/familiars/${id}.png`;
        familiarImageCache.set(id, img);
    }
    return img;
}

const canvasFamiliarParticleSystems = new Map<string, FamiliarParticleSystem>();

declare module "./types" {
    interface RenderablePlayer {
        petBuffActive?: boolean;
        petBuffType?: string;
        petBuffUntil?: number;
    }
    interface InterpolatedPlayer {
        petBuffActive?: boolean;
        petBuffType?: string;
        petBuffUntil?: number;
    }
}

export type { RenderablePlayer };

export interface FoodRenderable {
    x: number;
    y: number;
    /** 0=circulo, 1=cuadrado, 2=triangulo, 3=rombo. Derivado del hash del foodId. */
    shape: number;
}

export const FOOD_COLORS = ["#ef4444", "#38bdf8", "#22c55e", "#facc15", "#d97706"];
export const FOOD_RADIUS = 5.5;

export function foodShapeFromId(foodId: string): number {
    return hashString(foodId) % 5;
}

function drawFoodShape(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    r: number,
    shape: number,
    nowMs: number
): void {
    const phase = ((x * 0.13 + y * 0.17) % (Math.PI * 2));
    const pulse = 1 + Math.sin(nowMs * 0.005 + phase) * 0.14;
    const rad = r * pulse;

    ctx.save();
    ctx.translate(x, y);

    switch (shape) {
        case 0: {
            // 0: Brasa Ígnea (Flama danzante con núcleo amarillo)
            const sway = Math.sin(nowMs * 0.008 + phase) * (rad * 0.25);
            ctx.fillStyle = "#ef4444";
            ctx.beginPath();
            ctx.moveTo(sway, -rad * 1.45);
            ctx.quadraticCurveTo(rad * 1.15, 0, 0, rad * 0.95);
            ctx.quadraticCurveTo(-rad * 1.15, 0, sway, -rad * 1.45);
            ctx.fill();

            ctx.fillStyle = "#fef08a";
            ctx.beginPath();
            ctx.arc(0, rad * 0.15, rad * 0.45, 0, Math.PI * 2);
            ctx.fill();
            break;
        }
        case 1: {
            // 1: Gota de Rocío (Gota líquida con reflejo blanco)
            ctx.fillStyle = "#38bdf8";
            ctx.beginPath();
            ctx.arc(0, rad * 0.22, rad * 0.82, 0, Math.PI);
            ctx.quadraticCurveTo(-rad * 0.82, -rad * 0.4, 0, -rad * 1.25);
            ctx.quadraticCurveTo(rad * 0.82, -rad * 0.4, rad * 0.82, rad * 0.22);
            ctx.fill();

            ctx.fillStyle = "#ffffff";
            ctx.beginPath();
            ctx.arc(-rad * 0.26, rad * 0.05, rad * 0.24, 0, Math.PI * 2);
            ctx.fill();
            break;
        }
        case 2: {
            // 2: Hoja Viva (Hoja esmeralda con nervadura central)
            ctx.rotate(Math.sin(nowMs * 0.003 + phase) * 0.35 + phase);
            ctx.fillStyle = "#22c55e";
            ctx.beginPath();
            ctx.moveTo(-rad * 1.15, 0);
            ctx.quadraticCurveTo(0, -rad * 0.95, rad * 1.15, 0);
            ctx.quadraticCurveTo(0, rad * 0.95, -rad * 1.15, 0);
            ctx.fill();

            ctx.strokeStyle = "#bbf7d0";
            ctx.lineWidth = Math.max(1, rad * 0.18);
            ctx.beginPath();
            ctx.moveTo(-rad * 0.75, 0);
            ctx.lineTo(rad * 0.75, 0);
            ctx.stroke();
            break;
        }
        case 3: {
            // 3: Chispa Voltaica (Estrella eléctrica de 4 puntas rotando)
            ctx.rotate(nowMs * 0.004 + phase);
            ctx.fillStyle = "#facc15";
            ctx.beginPath();
            for (let i = 0; i < 8; i++) {
                const dist = i % 2 === 0 ? rad * 1.4 : rad * 0.38;
                const ang = (i * Math.PI) / 4;
                const px = Math.cos(ang) * dist;
                const py = Math.sin(ang) * dist;
                if (i === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.fill();

            ctx.fillStyle = "#ffffff";
            ctx.beginPath();
            ctx.arc(0, 0, rad * 0.32, 0, Math.PI * 2);
            ctx.fill();
            break;
        }
        case 4:
        default: {
            // 4: Cristal Mineral (Gema hexagonal facetada ámbar)
            ctx.rotate(phase);
            ctx.fillStyle = "#d97706";
            ctx.strokeStyle = "#fde68a";
            ctx.lineWidth = Math.max(1, rad * 0.2);
            ctx.beginPath();
            for (let i = 0; i < 6; i++) {
                const ang = (i * Math.PI) / 3;
                const dist = i % 2 === 0 ? rad * 1.15 : rad * 0.9;
                const px = Math.cos(ang) * dist;
                const py = Math.sin(ang) * dist;
                if (i === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            break;
        }
    }

    ctx.restore();
}

/** Paleta de colores distintivos por elemento */
export const ELEMENT_COLORS: Record<string, string> = {
    fire: "#ef4444",
    water: "#3b82f6",
    plant: "#22c55e",
    electric: "#eab308",
    earth: "#a16207",
};

/**
 * Colores por tipo de efecto elemental, usados para el anillo y las partículas del impacto.
 */
export const ELEMENTAL_HIT_COLORS: Record<string, string> = {
    "water->fire": "#3b82f6",
    "fire->plant": "#ef4444",
    "plant->earth": "#22c55e",
    "earth->electric": "#a16207",
    "electric->water": "#eab308",
    "chain-electric": "#facc15",
    "shield-break": "#3b82f6",
    "freeze-burst": "#a5f3fc",
};

/** Radio de detección de amenazas elementales para el indicador de peligro (px) */
export const DANGER_RADIUS = 300;

/**
 * Renderer gestiona la pintura del marco del juego sobre el contexto 2D del canvas.
 */
export class Renderer {
    private readonly canvas: HTMLCanvasElement;
    private readonly ctx: CanvasRenderingContext2D;
    private readonly camera: Camera;
    private proximityParticles: ProximityParticle[] = [];
    private lastProximitySpawnAt: number = 0;
    private mapWidth: number = MAP_WIDTH;
    private mapHeight: number = MAP_HEIGHT;

    public setMapDimensions(width: number, height: number): void {
        if (width > 0 && height > 0) {
            this.mapWidth = width;
            this.mapHeight = height;
        }
    }

    constructor(canvas: HTMLCanvasElement, camera: Camera) {
        this.canvas = canvas;
        this.camera = camera;

        const context = canvas.getContext("2d", { alpha: false });
        if (!context) {
            throw new Error("No se pudo obtener el contexto 2D del canvas.");
        }
        this.ctx = context;
    }

    /** Dibuja el estado visual del juego completo (fondo, cuadrícula con culling, bordes, comidas y jugadores). */
    public render(
        players: RenderablePlayer[],
        foods: FoodRenderable[] = [],
        deaths: DeathAnimation[] = [],
        elementalHits: ElementalHitEffect[] = [],
        localSessionId: string | null = null,
        trails: Map<string, TrailPoint[]> = new Map(),
        absorbPulses: Map<string, number> = new Map(),
        nowMs: number = performance.now(),
        foodParticles: FoodParticle[] = [],
        respawnRings: RespawnRing[] = [],
        skin: SkinId | PlayerCosmeticsConfig = "default",
        powerUps: PowerUpRenderable[] = [],
        powerUpPickups: PowerUpPickupEffect[] = [],
        activeEvent: ClientEvent | null = null,
        meteors: MeteorRender[] = [],
        meteorImpacts: MeteorImpactEffect[] = [],
        gravityCaptures: GravityCaptureEffect[] = [],
        floatingTexts: FloatingCombatText[] = [],
        isAttractMode: boolean = false,
        activeEmotes?: Map<string, { emoteId: string; startTime: number; duration: number }>
    ): void {
        const { width, height } = this.camera.getViewport();
        const ctx = this.ctx;

        // 1. Limpieza de fondo con el color base del tema (más claro en menú/attract mode)
        ctx.fillStyle = isAttractMode ? "#111827" : "#0b0e14";
        ctx.fillRect(0, 0, width, height);

        this.drawAmbientParallax(ctx, width, height, nowMs, players);
        this.drawAlchemyArenaFloor(ctx, width, height, nowMs, isAttractMode);

        // 2. Límites del viewport para culling de la cuadrícula
        const bounds = this.getViewportBounds();

        // 3. Cuadrícula del mapa (cada 100 unidades de mundo)
        const gridSize = 100;
        const startX = Math.max(0, Math.floor(bounds.left / gridSize) * gridSize);
        const endX = Math.min(this.mapWidth, Math.ceil(bounds.right / gridSize) * gridSize);
        const startY = Math.max(0, Math.floor(bounds.top / gridSize) * gridSize);
        const endY = Math.min(this.mapHeight, Math.ceil(bounds.bottom / gridSize) * gridSize);

        ctx.strokeStyle = "rgba(255, 255, 255, 0.015)";
        ctx.lineWidth = 0.5;
        ctx.beginPath();

        const clampedTop = Math.max(0, bounds.top);
        const clampedBottom = Math.min(this.mapHeight, bounds.bottom);
        const clampedLeft = Math.max(0, bounds.left);
        const clampedRight = Math.min(this.mapWidth, bounds.right);

        for (let wx = startX; wx <= endX; wx += gridSize) {
            const p1 = this.camera.worldToScreen(wx, clampedTop);
            const p2 = this.camera.worldToScreen(wx, clampedBottom);
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
        }

        for (let wy = startY; wy <= endY; wy += gridSize) {
            const p1 = this.camera.worldToScreen(clampedLeft, wy);
            const p2 = this.camera.worldToScreen(clampedRight, wy);
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
        }
        ctx.stroke();

        // 4. Bordes del mapa de juego [0, MAP_WIDTH] x [0, MAP_HEIGHT] y Vacío Abisal
        const mapTopLeft = this.camera.worldToScreen(0, 0);
        const mapBottomRight = this.camera.worldToScreen(this.mapWidth, this.mapHeight);

        // Franjas exteriores del Vacío Abisal
        ctx.fillStyle = "rgba(2, 4, 12, 0.78)";
        if (mapTopLeft.y > 0) {
            ctx.fillRect(0, 0, width, mapTopLeft.y);
        }
        if (mapBottomRight.y < height) {
            ctx.fillRect(0, mapBottomRight.y, width, height - mapBottomRight.y);
        }
        if (mapTopLeft.x > 0) {
            const topY = Math.max(0, mapTopLeft.y);
            const bottomY = Math.min(height, mapBottomRight.y);
            if (bottomY > topY) {
                ctx.fillRect(0, topY, mapTopLeft.x, bottomY - topY);
            }
        }
        if (mapBottomRight.x < width) {
            const topY = Math.max(0, mapTopLeft.y);
            const bottomY = Math.min(height, mapBottomRight.y);
            if (bottomY > topY) {
                ctx.fillRect(mapBottomRight.x, topY, width - mapBottomRight.x, bottomY - topY);
            }
        }

        // Barrera Alquímica Neón en el contorno del mapa
        const mapRectW = mapBottomRight.x - mapTopLeft.x;
        const mapRectH = mapBottomRight.y - mapTopLeft.y;
        ctx.strokeStyle = "rgba(56, 189, 248, 0.22)";
        ctx.lineWidth = 8;
        ctx.strokeRect(mapTopLeft.x, mapTopLeft.y, mapRectW, mapRectH);
        ctx.strokeStyle = "rgba(56, 189, 248, 0.75)";
        ctx.lineWidth = 2.5;
        ctx.strokeRect(mapTopLeft.x, mapTopLeft.y, mapRectW, mapRectH);

        // 4.5. Estelas de movimiento temáticas (detras de comidas y jugadores)
        this.drawElementalTrails(ctx, trails, width, height, nowMs);

        // 5. Renderizado de comidas con culling de pantalla
        const foodZoom = this.camera.getZoom();
        const scaledFoodRadius = FOOD_RADIUS * foodZoom;
        for (let i = 0; i < foods.length; i++) {
            const food = foods[i];
            const screen = this.camera.worldToScreen(food.x, food.y);
            // Culling
            if (screen.x + scaledFoodRadius < 0 || screen.x - scaledFoodRadius > width ||
                screen.y + scaledFoodRadius < 0 || screen.y - scaledFoodRadius > height) continue;

            const baseColor = FOOD_COLORS[food.shape] || FOOD_COLORS[0];
            // Glow sutil (2 aros)
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, scaledFoodRadius + 3 * foodZoom, 0, Math.PI * 2);
            ctx.fillStyle = baseColor;
            ctx.globalAlpha = 0.15;
            ctx.fill();
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, scaledFoodRadius + 1.5 * foodZoom, 0, Math.PI * 2);
            ctx.globalAlpha = 0.3;
            ctx.fill();
            ctx.globalAlpha = 1.0;
            // Forma coloreada
            ctx.fillStyle = baseColor;
            drawFoodShape(ctx, screen.x, screen.y, scaledFoodRadius, food.shape, nowMs);
        }

        // 5.7. Renderizado de power-ups (objetos magicos)
        const pwZoom = this.camera.getZoom();
        for (let i = 0; i < powerUps.length; i++) {
            const pw = powerUps[i];
            const screen = this.camera.worldToScreen(pw.x, pw.y);
            const cullingMargin = 40 * pwZoom;
            // Culling
            if (screen.x + cullingMargin < 0 || screen.x - cullingMargin > width ||
                screen.y + cullingMargin < 0 || screen.y - cullingMargin > height) continue;

            const config = POWERUP_CONFIG[pw.type];
            if (!config) continue;
            const pulse = Math.sin(nowMs * 0.005) * 2;
            const radius = (POWERUP_RADIUS + pulse) * pwZoom;

            // Halo exterior (2 aros)
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, radius + 8 * pwZoom, 0, Math.PI * 2);
            ctx.fillStyle = config.color;
            ctx.globalAlpha = 0.15;
            ctx.fill();
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, radius + 4 * pwZoom, 0, Math.PI * 2);
            ctx.globalAlpha = 0.25;
            ctx.fill();
            ctx.globalAlpha = 1.0;

            // Cuerpo del power-up (circulo con color)
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
            ctx.fillStyle = config.color;
            ctx.globalAlpha = 0.85;
            ctx.fill();
            ctx.globalAlpha = 1.0;

            // Borde blanco
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
            ctx.strokeStyle = "rgba(255, 255, 255, 0.9)";
            ctx.lineWidth = Math.max(1, 2 * pwZoom);
            ctx.stroke();

            // Emoji del power-up en el centro
            const emojiSize = Math.max(10, Math.round(20 * pwZoom));
            ctx.font = `bold ${emojiSize}px system-ui, sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(config.emoji, screen.x, screen.y);
            ctx.textAlign = "start";
            ctx.textBaseline = "alphabetic";

            // Particulas orbitales (3 puntos girando)
            const orbCount = 3;
            for (let o = 0; o < orbCount; o++) {
                const angle = (nowMs * 0.002) + (o / orbCount) * Math.PI * 2;
                const orbX = screen.x + Math.cos(angle) * (radius + 6 * pwZoom);
                const orbY = screen.y + Math.sin(angle) * (radius + 6 * pwZoom);
                ctx.beginPath();
                ctx.arc(orbX, orbY, Math.max(1, 2 * pwZoom), 0, Math.PI * 2);
                ctx.fillStyle = config.color;
                ctx.globalAlpha = 0.7;
                ctx.fill();
                ctx.globalAlpha = 1.0;
            }
        }

        // 5.8. Anillo del evento activo
        if (activeEvent) {
            const center = this.camera.worldToScreen(activeEvent.x, activeEvent.y);
            const zoom = this.camera.getZoom();
            const screenRadius = activeEvent.radius * zoom;
            // Culling
            if (
                center.x + screenRadius > -100 &&
                center.x - screenRadius < width + 100 &&
                center.y + screenRadius > -100 &&
                center.y - screenRadius < height + 100
            ) {
                const pulse = 0.5 + 0.5 * Math.sin(nowMs * 0.004);
                // Anillo exterior
                ctx.strokeStyle = activeEvent.color;
                ctx.lineWidth = 4;
                ctx.globalAlpha = 0.5 + pulse * 0.3;
                ctx.beginPath();
                ctx.arc(center.x, center.y, screenRadius, 0, Math.PI * 2);
                ctx.stroke();
                // Anillo interior (dashed)
                ctx.setLineDash([12, 8]);
                ctx.lineWidth = 2;
                ctx.globalAlpha = 0.3 + pulse * 0.3;
                ctx.beginPath();
                ctx.arc(center.x, center.y, screenRadius - 8, 0, Math.PI * 2);
                ctx.stroke();
                ctx.setLineDash([]);
                // Texto flotante con el nombre del evento
                ctx.globalAlpha = 0.9;
                ctx.font = "bold 20px system-ui, sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillStyle = activeEvent.color;
                ctx.strokeStyle = "#000000";
                ctx.lineWidth = 4;
                const label = `${activeEvent.emoji} ${activeEvent.name.toUpperCase()}`;
                const labelY = center.y - screenRadius - 20;
                ctx.strokeText(label, center.x, labelY);
                ctx.fillText(label, center.x, labelY);
                ctx.textAlign = "start";
                ctx.textBaseline = "alphabetic";
                ctx.globalAlpha = 1.0;
            }
        }

        // Detección de amenaza mas cercana al jugador local y peligro de devoración
        let nearestThreatId: string | null = null;
        let nearestThreatDistSq = Infinity;
        let localPlayerDevourDanger: "none" | "limit" | "deadly" = "none";
        const nowEpoch = Date.now();
        const localPlayer = localSessionId ? players.find((p) => p.sessionId === localSessionId) : null;

        if (localPlayer && localPlayer.alive) {
            const dangerRadiusSq = DANGER_RADIUS * DANGER_RADIUS;
            const localWorldRadius = Math.sqrt(localPlayer.mass) * 2;
            for (const other of players) {
                if (other.sessionId === localSessionId) continue;
                if (!other.alive) continue;

                const dx = other.renderX - localPlayer.renderX;
                const dy = other.renderY - localPlayer.renderY;
                const distSq = dx * dx + dy * dy;

                const counterKey = `${other.element}->${localPlayer.element}`;
                if (ELEMENTAL_COUNTERS[counterKey] && distSq <= dangerRadiusSq && distSq < nearestThreatDistSq) {
                    nearestThreatDistSq = distSq;
                    nearestThreatId = other.sessionId;
                }

                // Detección de peligro de devoración para alertar en el núcleo del jugador local
                const canDevourLocal = other.mass >= localPlayer.mass * EAT_RATIO && localPlayer.invulnerableUntil <= nowEpoch;
                if (canDevourLocal) {
                    const worldDist = Math.sqrt(distSq);
                    const rivalWorldRadius = Math.sqrt(other.mass) * 2;
                    const touchWorldDist = rivalWorldRadius + localWorldRadius;
                    if (worldDist <= touchWorldDist + 25) {
                        if (worldDist <= rivalWorldRadius + localWorldRadius * 0.45) {
                            localPlayerDevourDanger = "deadly";
                        } else if (localPlayerDevourDanger !== "deadly") {
                            localPlayerDevourDanger = "limit";
                        }
                    }
                }
            }
        }

        // 6. Renderizado de jugadores con culling de pantalla
        const playerZoom = this.camera.getZoom();
        for (let i = 0; i < players.length; i++) {
            const player = players[i];
            if (!player.alive) continue;
            if (!Number.isFinite(player.renderX) || !Number.isFinite(player.renderY) || !Number.isFinite(player.mass) || player.mass <= 0) {
                continue;
            }
            let radius = Math.sqrt(player.mass) * 2 * playerZoom;
            // Pulso elástico al absorber jugador
            const pulseStart = absorbPulses.get(player.sessionId);
            if (pulseStart !== undefined) {
                const pulseProgress = (nowMs - pulseStart) / 300;
                if (pulseProgress >= 1) {
                    absorbPulses.delete(player.sessionId);
                } else {
                    radius *= 1 + Math.sin(pulseProgress * Math.PI) * 0.20;
                }
            }
            const screen = this.camera.worldToScreen(player.renderX, player.renderY);

            // Culling: saltar jugadores completamente fuera del viewport
            if (
                screen.x + radius < 0 ||
                screen.x - radius > width ||
                screen.y + radius < 0 ||
                screen.y - radius > height
            ) {
                continue;
            }

            const playerCosmetics = (player.isLocal && typeof skin === "object")
                ? skin
                : player.cosmetics;
            const skinId = (playerCosmetics as any)?.elementSkins?.[player.element] || getDefaultSkinForElement(player.element);
            const skinDrawn = drawCanvasElementSkin(ctx, screen.x, screen.y, radius, skinId, player.element, nowMs, playerZoom);
            if (!skinDrawn) {
                this.drawElementalBody(ctx, player, screen.x, screen.y, radius, playerZoom, nowMs);
            }
            try {
                drawModularSkin(ctx, screen.x, screen.y, radius, playerCosmetics, player.sessionId, player.facingAngle, nowMs, player.element);
            } catch (err) {
                console.warn("[Renderer] Error en skin:", err);
            }

            // Renderizado de Familiar Cosmético (Mascota) en Canvas 2D
            const familiarId = playerCosmetics?.familiar;
            if (familiarId && familiarId !== "none" && player.alive) {
                let partSystem = canvasFamiliarParticleSystems.get(player.sessionId);
                if (!partSystem) {
                    partSystem = new FamiliarParticleSystem();
                    canvasFamiliarParticleSystems.set(player.sessionId, partSystem);
                }

                const isBuffActive = Boolean(player.petBuffActive);
                const famElement = getFamiliarElement(familiarId);
                const elemColor = ELEMENT_COLORS[famElement] || "#38bdf8";

                // Aro elíptico holográfico horizontal alrededor del jugador cuando el poder está activo
                if (isBuffActive) {
                    const orbitDistX = Math.max(26, radius) + 32;
                    const orbitDistY = orbitDistX * 0.44;
                    const orbitAlpha = 0.35 + Math.sin(nowMs * 0.01) * 0.2;
                    ctx.save();
                    ctx.strokeStyle = elemColor;
                    ctx.lineWidth = 2;
                    ctx.globalAlpha = orbitAlpha;
                    ctx.beginPath();
                    ctx.ellipse(screen.x, screen.y, orbitDistX, orbitDistY, 0, 0, Math.PI * 2);
                    ctx.stroke();
                    ctx.restore();
                }

                const targetPos = getFamiliarTargetPosition(screen.x, screen.y, radius, nowMs, isBuffActive);

                // Emisión y actualización de partículas de estela
                const emitCount = isBuffActive ? 3 : 1;
                partSystem.emit(targetPos.x, targetPos.y, famElement, emitCount);
                partSystem.update(16.6);

                // Dibujar partículas con ctx.arc y alpha decreciente
                const particles = partSystem.getParticles();
                ctx.save();
                for (let pIdx = 0; pIdx < particles.length; pIdx++) {
                    const p = particles[pIdx];
                    ctx.globalAlpha = p.alpha;
                    ctx.fillStyle = isBuffActive && pIdx % 2 === 0 ? "#fbbf24" : p.color;
                    ctx.beginPath();
                    ctx.arc(p.x, p.y, Math.max(1, p.size * playerZoom), 0, Math.PI * 2);
                    ctx.fill();
                }
                ctx.restore();

                // Dibujar imagen del familiar con halo translúcido y tamaño proporcional
                const famImg = getFamiliarImage(familiarId);
                if (famImg && famImg.complete && famImg.naturalWidth > 0) {
                    const baseSize = Math.max(20, Math.min(40, 28 * playerZoom));
                    const breathScale = isBuffActive ? (1.0 + Math.sin(nowMs * 0.012) * 0.08) : 1.0;
                    const famSize = baseSize * breathScale;
                    const haloRadius = (famSize / 2) * (isBuffActive ? 1.55 : 1.35);
                    const haloAlpha = isBuffActive ? Math.max(0.05, Math.min(0.95, 0.4 + Math.sin(nowMs * 0.015) * 0.35)) : 0.25;

                    ctx.save();
                    ctx.fillStyle = isBuffActive && famElement === "water" ? "#67e8f9" : elemColor;
                    ctx.globalAlpha = haloAlpha;
                    ctx.beginPath();
                    ctx.arc(targetPos.x, targetPos.y, haloRadius, 0, Math.PI * 2);
                    ctx.fill();

                    if (isBuffActive) {
                        ctx.strokeStyle = "#fbbf24";
                        ctx.lineWidth = 1.5;
                        ctx.globalAlpha = Math.max(0.15, haloAlpha * 0.7);
                        ctx.beginPath();
                        ctx.arc(targetPos.x, targetPos.y, haloRadius * 1.25, 0, Math.PI * 2);
                        ctx.stroke();
                    }
                    ctx.restore();

                    ctx.drawImage(
                        famImg,
                        targetPos.x - famSize / 2,
                        targetPos.y - famSize / 2,
                        famSize,
                        famSize
                    );
                }
            } else {
                canvasFamiliarParticleSystems.delete(player.sessionId);
            }

            // Indicador de "Zona Segura de Robo" vs "Límite Mortal de Devoración"
            if (!player.isLocal && player.alive && localPlayer && localPlayer.alive) {
                const canDevourLocal = player.mass >= localPlayer.mass * EAT_RATIO && localPlayer.invulnerableUntil <= nowEpoch;
                if (canDevourLocal) {
                    const dx = player.renderX - localPlayer.renderX;
                    const dy = player.renderY - localPlayer.renderY;
                    const worldDist = Math.hypot(dx, dy);
                    const rivalWorldRadius = Math.sqrt(player.mass) * 2;
                    const localWorldRadius = Math.sqrt(localPlayer.mass) * 2;
                    const touchWorldDist = rivalWorldRadius + localWorldRadius;
                    const devourWorldDist = rivalWorldRadius;

                    if (worldDist <= touchWorldDist + 140) {
                        const proximity = Math.max(0, Math.min(1, 1 - (worldDist - devourWorldDist) / (touchWorldDist + 140 - devourWorldDist)));

                        ctx.save();

                        // A) Anillo Exterior de Zona de Robo Segura (si el local le hace Counter Elemental al rival gigante)
                        const counterKey = `${localPlayer.element}->${player.element}`;
                        if (ELEMENTAL_COUNTERS[counterKey]) {
                            const stealRadius = (rivalWorldRadius + localWorldRadius) * playerZoom;
                            ctx.beginPath();
                            ctx.arc(screen.x, screen.y, stealRadius, 0, Math.PI * 2);
                            ctx.strokeStyle = `rgba(52, 211, 153, ${0.25 + proximity * 0.35})`;
                            ctx.lineWidth = Math.max(1.5, 2.5 * playerZoom);
                            ctx.setLineDash([8 * playerZoom, 6 * playerZoom]);
                            ctx.stroke();
                        }

                        // B) Frontera Mortal de Devoración (sobre el borde exacto del rival)
                        const mortalRadius = rivalWorldRadius * playerZoom;
                        const redPulse = 0.6 + 0.4 * Math.sin(nowMs * 0.012);
                        ctx.beginPath();
                        ctx.arc(screen.x, screen.y, mortalRadius, 0, Math.PI * 2);
                        ctx.strokeStyle = `rgba(255, 23, 68, ${0.75 + redPulse * 0.25})`;
                        ctx.lineWidth = Math.max(2, 3.5 * playerZoom);
                        ctx.setLineDash([10 * playerZoom, 6 * playerZoom]);
                        ctx.shadowColor = "#ff1744";
                        ctx.shadowBlur = (8 + redPulse * 6) * playerZoom;
                        ctx.stroke();

                        ctx.restore();

                        // Si el jugador local ya está tocando o muy cerca de cruzar el umbral
                        if (worldDist <= touchWorldDist + 25) {
                            ctx.save();
                            const angle = Math.atan2(localPlayer.renderY - player.renderY, localPlayer.renderX - player.renderX);
                            const localScreen = this.camera.worldToScreen(localPlayer.renderX, localPlayer.renderY);
                            const rivalEdgeX = screen.x + Math.cos(angle) * mortalRadius;
                            const rivalEdgeY = screen.y + Math.sin(angle) * mortalRadius;

                            // 1. Línea tensora de advertencia entre borde enemigo y núcleo del jugador local
                            const isDeadlyClose = worldDist <= rivalWorldRadius + localWorldRadius * 0.45;
                            const tensorPulse = 0.45 + 0.45 * Math.sin(nowMs * 0.016);
                            ctx.beginPath();
                            ctx.moveTo(rivalEdgeX, rivalEdgeY);
                            ctx.lineTo(localScreen.x, localScreen.y);
                            ctx.strokeStyle = isDeadlyClose
                                ? `rgba(255, 23, 68, ${tensorPulse})`
                                : `rgba(245, 158, 11, ${tensorPulse})`;
                            ctx.lineWidth = Math.max(1.5, 2.2 * playerZoom);
                            ctx.setLineDash([4 * playerZoom, 4 * playerZoom]);
                            ctx.stroke();

                            // 2. Badge compacto y nítido en el punto medio entre ambos bordes
                            let badgeText = "";
                            let badgeColor = "#ffffff";
                            let badgeBg = "rgba(15, 23, 42, 0.92)";
                            let badgeBorder = "#f59e0b";

                            if (isDeadlyClose) {
                                badgeText = "💀 ¡MÁS CERCA MUERES!";
                                badgeColor = "#ffffff";
                                badgeBorder = "#ff1744";
                                badgeBg = "rgba(220, 38, 38, 0.94)";
                            } else if (worldDist <= touchWorldDist + 15) {
                                badgeText = "⚠️ LÍMITE DE DEVORACIÓN";
                                badgeColor = "#fef08a";
                                badgeBorder = "#f59e0b";
                                badgeBg = "rgba(15, 23, 42, 0.92)";
                            }

                            if (badgeText) {
                                const midWorldDist = (rivalWorldRadius + Math.max(devourWorldDist, worldDist - localWorldRadius)) / 2;
                                const midScreenX = screen.x + Math.cos(angle) * (midWorldDist * playerZoom);
                                const midScreenY = screen.y + Math.sin(angle) * (midWorldDist * playerZoom);

                                const fontSize = Math.max(10, Math.round(11 * playerZoom));
                                ctx.font = `800 ${fontSize}px system-ui, -apple-system, sans-serif`;
                                ctx.textAlign = "center";
                                ctx.textBaseline = "middle";

                                const textMetrics = ctx.measureText(badgeText);
                                const padX = 7 * playerZoom;
                                const padY = 3.5 * playerZoom;
                                const boxW = textMetrics.width + padX * 2;
                                const boxH = fontSize + padY * 2;
                                const boxX = midScreenX - boxW / 2;
                                const boxY = midScreenY - boxH / 2;

                                ctx.setLineDash([]);
                                ctx.fillStyle = badgeBg;
                                ctx.strokeStyle = badgeBorder;
                                ctx.lineWidth = Math.max(1.2, 1.8 * playerZoom);
                                ctx.shadowColor = badgeBorder;
                                ctx.shadowBlur = 6 * playerZoom;

                                const cornerR = 5 * playerZoom;
                                ctx.beginPath();
                                if (typeof ctx.roundRect === "function") {
                                    ctx.roundRect(boxX, boxY, boxW, boxH, cornerR);
                                } else {
                                    ctx.rect(boxX, boxY, boxW, boxH);
                                }
                                ctx.fill();
                                ctx.stroke();

                                ctx.shadowBlur = 0;
                                ctx.fillStyle = badgeColor;
                                ctx.fillText(badgeText, midScreenX, midScreenY);
                            }

                            ctx.restore();
                        }
                    }
                }
            }

            // Efecto de invulnerabilidad: cupula dorada
            if (player.invulnerableUntil > nowEpoch) {
                const pulse = 0.3 + 0.2 * Math.sin(nowMs * 0.008);
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius + 6 * playerZoom, 0, Math.PI * 2);
                ctx.strokeStyle = "#fbbf24";
                ctx.lineWidth = Math.max(1.5, 3 * playerZoom);
                ctx.globalAlpha = pulse;
                ctx.stroke();
                ctx.globalAlpha = 1.0;
            }

            // Efecto de escudo: anillo orbital discontinuo
            if (player.hasShield) {
                ctx.setLineDash([8, 6]);
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius + 5 * playerZoom, 0, Math.PI * 2);
                ctx.strokeStyle = "#3b82f6";
                ctx.lineWidth = Math.max(1, 2 * playerZoom);
                ctx.stroke();
                ctx.setLineDash([]);
            }

            // Efecto de freeze: tinte azul-hielo
            if (player.frozenUntil > nowEpoch) {
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius + 2 * playerZoom, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(165, 243, 252, 0.35)";
                ctx.fill();
            }

            // Sprint 29: Efecto de Bebiendo Poción (Vulnerable e Inmóvil)
            if (player.drinkingUntil > nowEpoch) {
                const remainingSec = Math.max(0, (player.drinkingUntil - nowEpoch) / 1000).toFixed(1);
                ctx.save();
                // Burbujas alquímicas ascendentes
                for (let i = 0; i < 5; i++) {
                    const bubblePhase = (nowMs * 0.003 + i * 1.3) % 1;
                    const bubbleAngle = (i * (Math.PI * 2 / 5)) + Math.sin(nowMs * 0.005 + i);
                    const bubbleDist = (radius * (0.6 + bubblePhase * 0.6)) * playerZoom;
                    const bx = screen.x + Math.cos(bubbleAngle) * bubbleDist;
                    const by = screen.y + Math.sin(bubbleAngle) * bubbleDist - bubblePhase * 25 * playerZoom;
                    const bRadius = (2.5 + (1 - bubblePhase) * 2.5) * playerZoom;

                    ctx.beginPath();
                    ctx.arc(bx, by, Math.max(1.5, bRadius), 0, Math.PI * 2);
                    ctx.fillStyle = "rgba(56, 189, 248, 0.75)";
                    ctx.fill();
                    ctx.strokeStyle = "rgba(255, 255, 255, 0.85)";
                    ctx.lineWidth = 1;
                    ctx.stroke();
                }

                // Texto/ícono indicando cast de bebida "🧪 0.5s"
                ctx.font = `bold ${Math.max(12, 14 * playerZoom)}px 'Outfit', sans-serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";
                ctx.fillStyle = "#facc15";
                ctx.shadowColor = "rgba(0, 0, 0, 0.8)";
                ctx.shadowBlur = 6;
                ctx.fillText(`🧪 ${remainingSec}s`, screen.x, screen.y - radius * playerZoom - 8);
                ctx.restore();
            }

            // Sprint 29: Efecto de Imán de Esencia / Mascota Eléctrica
            const hasPetMagnet = Boolean(player.petBuffActive && player.petBuffType === "magnet_radius");
            if (player.magnetUntil > nowEpoch || hasPetMagnet) {
                ctx.save();
                const magnetPulse = 0.28 + 0.16 * Math.sin(nowMs * 0.008);
                const isPetOnly = hasPetMagnet && player.magnetUntil <= nowEpoch;
                const ringStroke = isPetOnly ? `rgba(250, 204, 21, ${magnetPulse})` : `rgba(255, 64, 129, ${magnetPulse})`;
                const waveStroke = isPetOnly ? (alpha: number) => `rgba(56, 189, 248, ${alpha})` : (alpha: number) => `rgba(255, 64, 129, ${alpha})`;
                const magnetRadiusPx = (radius + (isPetOnly ? 100 : 260)) * playerZoom;

                ctx.beginPath();
                ctx.arc(screen.x, screen.y, magnetRadiusPx, 0, Math.PI * 2);
                ctx.strokeStyle = ringStroke;
                ctx.lineWidth = Math.max(1.5, 2.5 * playerZoom);
                ctx.setLineDash([8 * playerZoom, 6 * playerZoom]);
                ctx.stroke();

                // Onda concéntrica expansiva sutil
                const wavePhase = (nowMs * 0.0012) % 1;
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, (radius + (magnetRadiusPx / playerZoom - radius) * wavePhase) * playerZoom, 0, Math.PI * 2);
                ctx.strokeStyle = waveStroke((1 - wavePhase) * 0.28);
                ctx.lineWidth = Math.max(1, 1.5 * playerZoom);
                ctx.setLineDash([]);
                ctx.stroke();
                ctx.restore();
            }

            // Sprint 29: Efecto de Elixir de Velocidad (Halo cian eléctrico)
            if (player.speedBoostUntil > nowEpoch) {
                ctx.save();
                const pulse = 0.5 + 0.35 * Math.sin(nowMs * 0.012);
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius + 4 * playerZoom, 0, Math.PI * 2);
                ctx.strokeStyle = "#00e5ff";
                ctx.lineWidth = Math.max(1.8, 3.2 * playerZoom);
                ctx.shadowColor = "#00e5ff";
                ctx.shadowBlur = 14;
                ctx.globalAlpha = pulse;
                ctx.stroke();
                ctx.restore();
            }

            // Sprint 29: Bloqueo Elemental (Candado dorado sobre la célula)
            if (player.elementLockedUntil > nowEpoch) {
                ctx.save();
                ctx.font = `${Math.max(18, 22 * playerZoom)}px sans-serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.shadowColor = "rgba(0, 0, 0, 0.9)";
                ctx.shadowBlur = 8;
                ctx.fillText("🔒", screen.x, screen.y - (radius * 0.65) * playerZoom);
                ctx.restore();
            }

            // Efecto de propulsión alquímica (Barra Espaciadora - Sprint 14C)
            if (player.isBoosting) {
                const backAngle = player.facingAngle + Math.PI;
                ctx.save();
                for (let b = 1; b <= 2; b++) {
                    const waveDist = (radius * (0.8 + b * 0.45) + Math.sin(nowMs * 0.03 + b) * 4) * playerZoom;
                    const wx = screen.x + Math.cos(backAngle) * waveDist;
                    const wy = screen.y + Math.sin(backAngle) * waveDist;
                    const waveR = Math.max(3, (radius * (0.55 - b * 0.15)) * playerZoom);
                    ctx.beginPath();
                    ctx.arc(wx, wy, waveR, 0, Math.PI * 2);
                    ctx.strokeStyle = b === 1 ? "rgba(56, 189, 248, 0.75)" : "rgba(255, 255, 255, 0.85)";
                    ctx.lineWidth = Math.max(1.5, 2.5 * playerZoom);
                    ctx.stroke();
                }
                ctx.restore();
            }

            // Halo de Dominación / Racha de Asesinatos (Sprint 14C)
            if (player.killstreak >= 3) {
                ctx.save();
                const haloColor = player.killstreak >= 8 ? "#a855f7" : "#f59e0b";
                const pulse = 0.35 + 0.25 * Math.sin(nowMs * 0.007);
                ctx.globalAlpha = pulse;
                ctx.strokeStyle = haloColor;
                ctx.lineWidth = Math.max(1.8, 3.2 * playerZoom);
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius + (player.killstreak >= 8 ? 10 : 7) * playerZoom, 0, Math.PI * 2);
                ctx.stroke();
                ctx.restore();
            }

            // Borde punteado para bots
            if (player.isBot) {
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
                ctx.setLineDash([5, 5]);
                ctx.lineWidth = Math.max(1, 2 * playerZoom);
                ctx.strokeStyle = "rgba(255, 255, 255, 0.6)";
                ctx.stroke();
                ctx.setLineDash([]);
                // Etiqueta "BOT" debajo del circulo
                ctx.fillStyle = "rgba(255, 255, 255, 0.7)";
                ctx.font = `bold ${Math.max(8, Math.round(10 * playerZoom))}px system-ui, sans-serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "top";
                ctx.fillText("BOT", screen.x, screen.y + radius + 4 * playerZoom);
                // Reset estilos de texto
                ctx.textAlign = "start";
                ctx.textBaseline = "alphabetic";
            }

            if (player.isLocal) {
                // Realce del jugador local: borde blanco y núcleo interior
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
                ctx.lineWidth = Math.max(1.5, 3 * playerZoom);
                ctx.strokeStyle = "rgba(255, 255, 255, 0.45)";
                ctx.stroke();

                if (localPlayerDevourDanger !== "none") {
                    // Núcleo central parpadeante de advertencia: ámbar o rojo pulsante
                    const dotBlink = Math.sin(nowMs * 0.02) > 0;
                    const dangerColor = localPlayerDevourDanger === "deadly"
                        ? (dotBlink ? "#ff1744" : "#ff8a80")
                        : (dotBlink ? "#f59e0b" : "#fef08a");

                    ctx.save();
                    ctx.beginPath();
                    ctx.arc(screen.x, screen.y, Math.max(6, 10 * playerZoom), 0, Math.PI * 2);
                    ctx.strokeStyle = dangerColor;
                    ctx.lineWidth = Math.max(1.5, 2.5 * playerZoom);
                    ctx.setLineDash([3 * playerZoom, 3 * playerZoom]);
                    ctx.stroke();

                    ctx.beginPath();
                    ctx.arc(screen.x, screen.y, Math.max(3, 5 * playerZoom), 0, Math.PI * 2);
                    ctx.fillStyle = dangerColor;
                    ctx.shadowColor = dangerColor;
                    ctx.shadowBlur = 8 * playerZoom;
                    ctx.fill();
                    ctx.restore();
                } else {
                    ctx.beginPath();
                    ctx.arc(screen.x, screen.y, Math.max(2, 4 * playerZoom), 0, Math.PI * 2);
                    ctx.fillStyle = "#ffffff";
                    ctx.fill();
                }
            } else {
                // Jugadores remotos: sutil borde delimitador
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
                ctx.lineWidth = Math.max(1, 1.5 * playerZoom);
                ctx.strokeStyle = "rgba(255, 255, 255, 0.18)";
                ctx.stroke();
            }

            // Indicador de peligro: halo rojo pulsante sobre la amenaza mas cercana
            if (player.sessionId === nearestThreatId) {
                const pulseAlpha = 0.4 + 0.4 * Math.sin(performance.now() * 0.01);
                ctx.globalAlpha = pulseAlpha;
                ctx.strokeStyle = "#ef4444";
                ctx.lineWidth = Math.max(1.5, 3 * playerZoom);
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, radius + 8 * playerZoom, 0, Math.PI * 2);
                ctx.stroke();
                ctx.globalAlpha = 1.0;
            }

            // Alias sobre el circulo (solo si el circulo es grande suficiente)
            if (radius > 15 && player.alias.length > 0) {
                const prefix = player.killstreak >= 8 ? `👑${player.killstreak} ` : player.killstreak >= 3 ? `🔥${player.killstreak} ` : "";
                const displayAlias = `${prefix}${player.alias}`;
                const fontSize = Math.max(9, Math.round(12 * playerZoom));
                ctx.font = `bold ${fontSize}px system-ui, sans-serif`;
                ctx.textAlign = "center";
                ctx.textBaseline = "bottom";
                ctx.lineWidth = Math.max(1.5, 3 * playerZoom);
                ctx.strokeStyle = "#000000";
                ctx.strokeText(displayAlias, screen.x, screen.y - radius - 4 * playerZoom);
                ctx.fillStyle = "#ffffff";
                ctx.fillText(displayAlias, screen.x, screen.y - radius - 4 * playerZoom);
                // Reset alineaciones para no afectar otros renders
                ctx.textAlign = "start";
                ctx.textBaseline = "alphabetic";
            }

            // Sprint 30: Renderizado del Globo de Cómic Neón para Emotes Sociales
            const activeEmote = activeEmotes?.get(player.sessionId);
            if (activeEmote && (nowMs - activeEmote.startTime) < activeEmote.duration) {
                const elapsed = nowMs - activeEmote.startTime;
                const progress = elapsed / activeEmote.duration; // 0 a 1

                // Animación "Pop" y Fade
                let scale = 1.0;
                if (elapsed <= 100) {
                    scale = (elapsed / 100) * 1.2;
                } else if (elapsed <= 180) {
                    scale = 1.2 - ((elapsed - 100) / 80) * 0.2;
                } else {
                    scale = 1.0;
                }

                let alpha = 1.0;
                if (progress > 0.84) {
                    alpha = Math.max(0, 1 - (progress - 0.84) / 0.16);
                }
                if (!player.isLocal) {
                    alpha *= 0.80; // Transparencia anti-ruido visual para jugadores remotos
                }

                if (scale > 0.01 && alpha > 0.01) {
                    const floatOffset = progress * 18 * playerZoom;
                    const bubbleY = screen.y - radius - (36 * playerZoom) - floatOffset;
                    const bubbleX = screen.x;
                    const baseZoomClamped = Math.max(0.75, Math.min(1.25, playerZoom));
                    const bubbleRadius = 22 * baseZoomClamped * scale;

                    ctx.save();
                    ctx.globalAlpha = alpha;

                    const def = EMOTE_CATALOG[activeEmote.emoteId];
                    const isExclusive = def?.tier === "exclusive";
                    const neonColor = isExclusive ? "#fbbf24" : "#38bdf8";
                    const shadowBlur = (isExclusive ? 12 : 8) * playerZoom;

                    // Dibujar colita cómic apuntando hacia la célula
                    const tailH = 7 * baseZoomClamped * scale;
                    const tailW = 5 * baseZoomClamped * scale;
                    ctx.beginPath();
                    ctx.moveTo(bubbleX - tailW, bubbleY + bubbleRadius - 2);
                    ctx.lineTo(bubbleX, bubbleY + bubbleRadius + tailH);
                    ctx.lineTo(bubbleX + tailW, bubbleY + bubbleRadius - 2);
                    ctx.closePath();
                    ctx.fillStyle = "rgba(11, 15, 25, 0.90)";
                    ctx.fill();

                    // Burbuja redondeada
                    ctx.beginPath();
                    ctx.arc(bubbleX, bubbleY, bubbleRadius, 0, Math.PI * 2);
                    ctx.fillStyle = "rgba(11, 15, 25, 0.90)";
                    ctx.shadowColor = neonColor;
                    ctx.shadowBlur = shadowBlur;
                    ctx.fill();

                    // Borde neón
                    ctx.strokeStyle = neonColor;
                    ctx.lineWidth = Math.max(1.5, 2.2 * playerZoom);
                    ctx.stroke();

                    // Borde en la colita
                    ctx.beginPath();
                    ctx.moveTo(bubbleX - tailW, bubbleY + bubbleRadius - 1);
                    ctx.lineTo(bubbleX, bubbleY + bubbleRadius + tailH);
                    ctx.lineTo(bubbleX + tailW, bubbleY + bubbleRadius - 1);
                    ctx.strokeStyle = neonColor;
                    ctx.lineWidth = Math.max(1.2, 1.8 * playerZoom);
                    ctx.stroke();

                    // 3 pequeñas chispas doradas orbitando el globo si es tier exclusive
                    if (isExclusive) {
                        ctx.shadowBlur = 0;
                        const sparkCount = 3;
                        for (let s = 0; s < sparkCount; s++) {
                            const sparkAngle = (nowMs * 0.004) + (s * (Math.PI * 2 / sparkCount));
                            const sparkDist = bubbleRadius + 6 * baseZoomClamped;
                            const sx = bubbleX + Math.cos(sparkAngle) * sparkDist;
                            const sy = bubbleY + Math.sin(sparkAngle) * sparkDist;
                            ctx.beginPath();
                            ctx.arc(sx, sy, Math.max(1.2, 2 * playerZoom), 0, Math.PI * 2);
                            ctx.fillStyle = "#fef08a";
                            ctx.fill();
                        }
                    }

                    // Dibujar imagen SVG de Twemoji precargada en el centro del globo
                    ctx.shadowBlur = 0;
                    const { image: emoteImg, isLoaded } = emoteManager.getEmoteImage(activeEmote.emoteId);
                    const iconSize = bubbleRadius * 1.35;
                    if (isLoaded && emoteImg) {
                        ctx.drawImage(
                            emoteImg,
                            bubbleX - iconSize / 2,
                            bubbleY - iconSize / 2,
                            iconSize,
                            iconSize
                        );
                    } else if (def?.icon) {
                        // Fallback con el emoji nativo inmediato
                        ctx.font = `${Math.round(iconSize * 0.9)}px system-ui, sans-serif`;
                        ctx.textAlign = "center";
                        ctx.textBaseline = "middle";
                        ctx.fillText(def.icon, bubbleX, bubbleY + 1);
                    }

                    ctx.restore();
                }
            }
        }

        // 6.5. Microinteracciones reactivas de proximidad entre elementos
        this.processAndDrawProximityReactions(ctx, players, width, height, nowMs);

        // 7. Renderizado de animaciones de muerte (implosión elemental + fragmentos)
        const now = performance.now();
        const deathZoom = this.camera.getZoom();
        for (let i = 0; i < deaths.length; i++) {
            const death = deaths[i];
            const elapsed = now - death.startTime;
            const progress = Math.min(1, elapsed / DEATH_ANIMATION_MS);
            const eased = 1 - Math.pow(1 - progress, 3);
            const currentRadius = death.startRadius * (1 - eased) * deathZoom;
            const screen = this.camera.worldToScreen(death.x, death.y);

            const maxSpread = (death.startRadius * 1.4) * deathZoom;
            if (
                screen.x + maxSpread < 0 ||
                screen.x - maxSpread > width ||
                screen.y + maxSpread < 0 ||
                screen.y - maxSpread > height
            ) {
                continue;
            }

            const baseColor = ELEMENT_COLORS[death.element] || "#888888";
            ctx.save();

            // Onda de choque de colapso
            ctx.globalAlpha = (1 - progress) * 0.75;
            ctx.strokeStyle = baseColor;
            ctx.lineWidth = Math.max(1.5, 4 * (1 - progress) * deathZoom);
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, death.startRadius * (0.4 + progress * 0.9) * deathZoom, 0, Math.PI * 2);
            ctx.stroke();

            // Núcleo en contracción
            if (currentRadius > 0.5) {
                ctx.globalAlpha = 1 - eased;
                ctx.fillStyle = baseColor;
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, currentRadius, 0, Math.PI * 2);
                ctx.fill();
            }

            // 10 Fragmentos elementales eyectados al morir
            const shardCount = 10;
            for (let s = 0; s < shardCount; s++) {
                const ang = (s / shardCount) * Math.PI * 2 + (s % 2) * 0.25;
                const dist = (death.startRadius * 0.25 + progress * death.startRadius * 1.1) * deathZoom;
                const sx = screen.x + Math.cos(ang) * dist;
                const sy = screen.y + Math.sin(ang) * dist;
                const shardR = Math.max(1.5, (death.startRadius * 0.14 * (1 - progress)) * deathZoom);

                ctx.globalAlpha = 1 - progress;
                if (death.element === "electric") {
                    ctx.strokeStyle = s % 2 === 0 ? "#ffffff" : "#facc15";
                    ctx.lineWidth = 2;
                    ctx.beginPath();
                    ctx.moveTo(screen.x + Math.cos(ang) * dist * 0.5, screen.y + Math.sin(ang) * dist * 0.5);
                    ctx.lineTo(sx, sy);
                    ctx.stroke();
                } else if (death.element === "earth") {
                    ctx.fillStyle = s % 2 === 0 ? "#b45309" : "#fde68a";
                    ctx.fillRect(sx - shardR, sy - shardR, shardR * 2, shardR * 2);
                } else {
                    ctx.fillStyle = death.element === "fire" && s % 2 === 0 ? "#fef08a" : baseColor;
                    ctx.beginPath();
                    ctx.arc(sx, sy, shardR, 0, Math.PI * 2);
                    ctx.fill();
                }
            }

            ctx.restore();
        }
        ctx.globalAlpha = 1.0;

        // 7.5. Anillos de respawn (expansivos)
        for (let i = 0; i < respawnRings.length; i++) {
            const ring = respawnRings[i];
            const progress = Math.min(1, (nowMs - ring.startTime) / ring.duration);
            const radius = (10 + progress * 70) * this.camera.getZoom();
            const alpha = 1 - progress;
            const screen = this.camera.worldToScreen(ring.x, ring.y);
            ctx.globalAlpha = alpha;
            ctx.strokeStyle = ring.color;
            ctx.lineWidth = 3 * (1 - progress) + 1;
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
            ctx.stroke();
        }
        ctx.globalAlpha = 1.0;

        // 7.6. Efectos de eventos negativos (zona continua)
        this.drawPoisonZone(ctx, activeEvent, nowMs);
        this.drawGravityWell(ctx, activeEvent, nowMs);

        // 8. Renderizado de efectos elementales (anillo + partículas + texto de dano)
        const nowHit = performance.now();
        for (let i = 0; i < elementalHits.length; i++) {
            const hit = elementalHits[i];
            const elapsed = nowHit - hit.startTime;
            const progress = Math.min(1, elapsed / hit.duration);
            if (progress >= 1) continue;
            const screen = this.camera.worldToScreen(hit.x, hit.y);
            const color = ELEMENTAL_HIT_COLORS[hit.effectType] || "#ffffff";
            // Culling: si el efecto esta muy fuera del viewport, skip
            if (
                screen.x < -200 || screen.x > width + 200 ||
                screen.y < -200 || screen.y > height + 200
            ) {
                continue;
            }

            if (hit.effectType === "freeze-burst") {
                // Circulo grande con el radio del freeze
                const radius = hit.damage * this.camera.getZoom(); // El radio guardado
                const ringRadius = radius * progress;
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, ringRadius, 0, Math.PI * 2);
                ctx.strokeStyle = "#a5f3fc";
                ctx.lineWidth = 4 * (1 - progress);
                ctx.globalAlpha = 1 - progress;
                ctx.stroke();
                // Relleno interior con tinte
                ctx.fillStyle = "rgba(165, 243, 252, 0.1)";
                ctx.fill();
                ctx.globalAlpha = 1.0;
                continue;
            }

            // 8a. Anillo expansivo (fade out)
            const ringRadius = 20 + progress * 40;
            const ringAlpha = (1 - progress) * 0.8;
            ctx.globalAlpha = ringAlpha;
            ctx.strokeStyle = color;
            ctx.lineWidth = 3 * (1 - progress) + 1;
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, ringRadius, 0, Math.PI * 2);
            ctx.stroke();

            // Flash central explosivo durante el primer 25% del impacto
            if (progress < 0.25) {
                const flashProgress = progress / 0.25;
                const flashRadius = (22 * (1 - flashProgress) + 6) * this.camera.getZoom();
                ctx.save();
                ctx.globalAlpha = (1 - flashProgress) * 0.85;
                ctx.fillStyle = "#ffffff";
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, flashRadius, 0, Math.PI * 2);
                ctx.fill();
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, flashRadius * 1.5, 0, Math.PI * 2);
                ctx.globalAlpha = (1 - flashProgress) * 0.45;
                ctx.fill();
                ctx.restore();
            }

            // 8b. Partículas radiales (16 chispas en dos anillos alternados)
            const particleCount = 16;
            ctx.fillStyle = color;
            for (let p = 0; p < particleCount; p++) {
                const angle = (p / particleCount) * Math.PI * 2 + hit.startTime * 0.001;
                const particleDistance = p % 2 === 0 ? progress * 65 : progress * 38;
                const px = screen.x + Math.cos(angle) * particleDistance;
                const py = screen.y + Math.sin(angle) * particleDistance;
                const size = (p % 2 === 0 ? 3.5 : 2.5) * (1 - progress);
                if (size <= 0.5) continue;
                ctx.beginPath();
                ctx.arc(px, py, size, 0, Math.PI * 2);
                ctx.fill();
            }

            this.drawSpecializedElementalBurst(ctx, screen.x, screen.y, hit.effectType, progress, color, hit.startTime);

            if (hit.damage > 0) {
                // 8c. Texto flotante con el dano
                const textAlpha = 1 - progress;
                const textY = screen.y - 30 - progress * 30;
                ctx.globalAlpha = textAlpha;
                ctx.fillStyle = "#ffffff";
                ctx.strokeStyle = "#000000";
                ctx.lineWidth = 3;
                ctx.font = "bold 16px system-ui, sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                const damageText = `-${Math.round(hit.damage)}`;
                ctx.strokeText(damageText, screen.x, textY);
                ctx.fillText(damageText, screen.x, textY);
                // Reset
                ctx.globalAlpha = 1.0;
            }
        }

        // 8.1. Efectos de eventos negativos (impactos y capturas)
        this.drawMeteors(ctx, meteors, nowMs);
        this.drawMeteorImpacts(ctx, meteorImpacts, nowMs);
        this.drawGravityCaptures(ctx, gravityCaptures, nowMs);

        // 8.5. Partículas de comida con succión magnética
        for (let i = 0; i < foodParticles.length; i++) {
            const p = foodParticles[i];
            const progress = Math.min(1, (nowMs - p.startTime) / p.duration);
            if (progress >= 1) continue;
            const age = progress;
            let px = p.x + p.vx * progress * 40;
            let py = p.y + p.vy * progress * 40;

            if (p.targetSessionId) {
                const targetPlayer = players.find((pl) => pl.sessionId === p.targetSessionId);
                if (targetPlayer && targetPlayer.alive) {
                    const pull = progress * progress;
                    px = px * (1 - pull) + targetPlayer.renderX * pull;
                    py = py * (1 - pull) + targetPlayer.renderY * pull;
                }
            }

            const screen = this.camera.worldToScreen(px, py);
            const size = Math.max(1, (3.5 * (1 - age * 0.6)) * this.camera.getZoom());
            ctx.globalAlpha = 1 - age * 0.8;
            ctx.fillStyle = p.color;
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, size, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.globalAlpha = 1.0;

        // 8.6. Particulas de pickup de power-ups
        for (let i = 0; i < powerUpPickups.length; i++) {
            const eff = powerUpPickups[i];
            const elapsed = nowMs - eff.startTime;
            const progress = Math.min(1, elapsed / eff.duration);
            if (progress >= 1) continue;

            const config = POWERUP_CONFIG[eff.type];
            if (!config) continue;
            const screen = this.camera.worldToScreen(eff.x, eff.y);
            const particleCount = 12;
            const distance = progress * 50;

            for (let p = 0; p < particleCount; p++) {
                const angle = (p / particleCount) * Math.PI * 2;
                const px = screen.x + Math.cos(angle) * distance;
                const py = screen.y + Math.sin(angle) * distance;
                const size = 3 * (1 - progress);
                if (size <= 0.3) continue;
                ctx.beginPath();
                ctx.arc(px, py, size, 0, Math.PI * 2);
                ctx.fillStyle = config.color;
                ctx.globalAlpha = 1 - progress;
                ctx.fill();
            }
            ctx.globalAlpha = 1.0;
        }

        // 8.7. Textos flotantes de Combos y Shutdown Bounty (Sprint 14C)
        for (let i = 0; i < floatingTexts.length; i++) {
            const ft = floatingTexts[i];
            const progress = Math.min(1, (nowMs - ft.startTime) / ft.duration);
            if (progress >= 1) continue;
            const screen = this.camera.worldToScreen(ft.x, ft.y);
            const floatY = screen.y - 25 - progress * 45;
            const scale = progress < 0.2 ? 0.8 + (progress / 0.2) * 0.4 : 1.2 - (progress - 0.2) * 0.25;

            ctx.save();
            ctx.translate(screen.x, floatY);
            ctx.scale(scale, scale);
            ctx.globalAlpha = 1 - Math.pow(progress, 2);
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            ctx.font = "900 18px system-ui, sans-serif";
            ctx.strokeStyle = "rgba(0, 0, 0, 0.85)";
            ctx.lineWidth = 4;
            ctx.strokeText(ft.text, 0, 0);
            ctx.fillStyle = ft.color;
            ctx.fillText(ft.text, 0, 0);

            if (ft.subtext) {
                ctx.font = "700 12px system-ui, sans-serif";
                ctx.strokeText(ft.subtext, 0, 18);
                ctx.fillStyle = "#f8fafc";
                ctx.fillText(ft.subtext, 0, 18);
            }
            ctx.restore();
        }

        this.drawMinimap(ctx, players, localSessionId, activeEvent, width, height, nowMs);

        ctx.textAlign = "start";
        ctx.textBaseline = "alphabetic";
    }

    /** Dibuja sombras de advertencia y meteoritos cayendo */
    private drawMeteors(
        ctx: CanvasRenderingContext2D,
        meteors: MeteorRender[],
        _nowMs: number
    ): void {
        if (!meteors || meteors.length === 0) return;
        const { width, height } = this.camera.getViewport();
        const zoom = this.camera.getZoom();
        const screenRadius = METEOR_IMPACT_RADIUS * zoom;
        const nowEpoch = Date.now();

        for (let i = 0; i < meteors.length; i++) {
            const meteor = meteors[i];
            const screen = this.camera.worldToScreen(meteor.x, meteor.y);

            // Culling
            if (
                screen.x + screenRadius < -50 ||
                screen.x - screenRadius > width + 50 ||
                screen.y + screenRadius < -50 ||
                screen.y - screenRadius > height + 50
            ) {
                continue;
            }

            // t = progress from spawn to impact: 0 to 1
            const warningStart = meteor.impactAt - METEOR_WARNING_MS;
            const t = Math.max(0, Math.min(1, (nowEpoch - warningStart) / METEOR_WARNING_MS));

            if (meteor.impacted || t >= 1) {
                // Impactado: círculo rojo opaco con núcleo
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, screenRadius, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(239, 68, 68, 0.7)";
                ctx.fill();
                ctx.strokeStyle = "#ef4444";
                ctx.lineWidth = Math.max(1.5, 3 * zoom);
                ctx.stroke();

                ctx.beginPath();
                ctx.arc(screen.x, screen.y, screenRadius * 0.4, 0, Math.PI * 2);
                ctx.fillStyle = "#f87171";
                ctx.fill();
            } else {
                // Pre-impacto: sombra en el suelo con countdown
                // 1. Área de impacto completa translúcida (fondo rojo oscuro)
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, screenRadius, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(127, 29, 29, 0.25)";
                ctx.fill();
                ctx.strokeStyle = "rgba(239, 68, 68, 0.5)";
                ctx.lineWidth = Math.max(1, 2 * zoom);
                ctx.stroke();

                // 2. Indicador circular creciente (countdown)
                const countdownRadius = screenRadius * t;
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, countdownRadius, 0, Math.PI * 2);
                ctx.fillStyle = "rgba(239, 68, 68, 0.35)";
                ctx.fill();
                ctx.strokeStyle = "#ef4444";
                ctx.lineWidth = Math.max(1, 1.5 * zoom);
                ctx.stroke();

                // 3. Pequeña cruz central de alerta
                const crossSize = 6 * zoom;
                ctx.strokeStyle = "rgba(239, 68, 68, 0.7)";
                ctx.lineWidth = 1.5;
                ctx.beginPath();
                ctx.moveTo(screen.x - crossSize, screen.y);
                ctx.lineTo(screen.x + crossSize, screen.y);
                ctx.moveTo(screen.x, screen.y - crossSize);
                ctx.lineTo(screen.x, screen.y + crossSize);
                ctx.stroke();
            }
        }
        ctx.globalAlpha = 1.0;
    }

    /** Dibuja explosiones de impacto de meteorito */
    private drawMeteorImpacts(
        ctx: CanvasRenderingContext2D,
        meteorImpacts: MeteorImpactEffect[],
        nowMs: number
    ): void {
        if (!meteorImpacts || meteorImpacts.length === 0) return;
        const { width, height } = this.camera.getViewport();
        const zoom = this.camera.getZoom();

        for (let i = 0; i < meteorImpacts.length; i++) {
            const effect = meteorImpacts[i];
            const progress = Math.max(0, Math.min(1, (nowMs - effect.startTime) / effect.duration));
            if (progress >= 1) continue;

            const screen = this.camera.worldToScreen(effect.x, effect.y);
            const radius = METEOR_IMPACT_RADIUS * (0.5 + progress * 1.5) * zoom;
            const alpha = 1 - progress;

            // Culling
            if (
                screen.x + radius < 0 ||
                screen.x - radius > width ||
                screen.y + radius < 0 ||
                screen.y - radius > height
            ) {
                continue;
            }

            // Anillo naranja/rojo expansivo con lineWidth decreciente
            ctx.save();
            ctx.globalAlpha = alpha;
            ctx.strokeStyle = progress < 0.4 ? "#f97316" : "#ef4444";
            ctx.lineWidth = Math.max(1, (6 * (1 - progress) + 1) * zoom);
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
            ctx.stroke();

            // Flash central brillante
            const flashRadius = METEOR_IMPACT_RADIUS * 0.5 * (1 - progress) * zoom;
            if (flashRadius > 0) {
                ctx.fillStyle = "#fef08a";
                ctx.globalAlpha = alpha * 0.8;
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, flashRadius, 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.restore();
        }
    }

    /** Dibuja el relleno verde translúcido pulsante y partículas de la zona de veneno */
    private drawPoisonZone(
        ctx: CanvasRenderingContext2D,
        activeEvent: ClientEvent | null,
        nowMs: number
    ): void {
        if (!activeEvent || activeEvent.type !== "poison-zone") return;

        const center = this.camera.worldToScreen(activeEvent.x, activeEvent.y);
        const zoom = this.camera.getZoom();
        const screenRadius = activeEvent.radius * zoom;
        const { width, height } = this.camera.getViewport();

        // Culling
        if (
            center.x + screenRadius < 0 ||
            center.x - screenRadius > width ||
            center.y + screenRadius < 0 ||
            center.y - screenRadius > height
        ) {
            return;
        }

        ctx.save();

        // 1. Relleno verde translúcido con alpha pulsante: alpha = 0.15 + 0.1 * sin(now / 200)
        const pulseAlpha = Math.max(0.05, 0.15 + 0.1 * Math.sin(nowMs / 200));
        ctx.fillStyle = `rgba(34, 197, 94, ${pulseAlpha})`;
        ctx.beginPath();
        ctx.arc(center.x, center.y, screenRadius, 0, Math.PI * 2);
        ctx.fill();

        // 2. Borde verde más sólido
        ctx.strokeStyle = "rgba(34, 197, 94, 0.85)";
        ctx.lineWidth = Math.max(2, 3 * zoom);
        ctx.stroke();

        // 3. Partículas flotantes: 25 puntos verdes que flotan dentro del círculo
        const particleCount = 25;
        for (let i = 0; i < particleCount; i++) {
            const baseAngle = (i * 137.5 * Math.PI) / 180;
            const baseDist = Math.sqrt((i + 1) / particleCount) * (screenRadius * 0.85);
            const driftAngle = baseAngle + Math.sin(nowMs * 0.001 + i) * 0.35;
            const driftDist = baseDist + Math.cos(nowMs * 0.0015 + i * 2) * 12 * zoom;

            const px = center.x + Math.cos(driftAngle) * driftDist;
            const py = center.y + Math.sin(driftAngle) * driftDist;
            const particleAlpha = 0.25 + 0.25 * Math.sin(nowMs * 0.003 + i);
            const particleSize = Math.max(1.5, (2.5 + (i % 3)) * zoom);

            ctx.fillStyle = `rgba(74, 222, 128, ${Math.max(0, particleAlpha)})`;
            ctx.beginPath();
            ctx.arc(px, py, particleSize, 0, Math.PI * 2);
            ctx.fill();
        }

        // 4. Texto flotante en el centro
        ctx.font = `bold ${Math.max(14, Math.round(18 * zoom))}px system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#4ade80";
        ctx.strokeStyle = "#052e16";
        ctx.lineWidth = 3;
        const label = `☠ ${t("events.poisonZone", { defaultValue: "POISON ZONE" }).toUpperCase()}`;
        ctx.strokeText(label, center.x, center.y);
        ctx.fillText(label, center.x, center.y);

        ctx.restore();
    }

    /** Dibuja el pozo gravitatorio con espiral/anillos violetas y atracción */
    private drawGravityWell(
        ctx: CanvasRenderingContext2D,
        activeEvent: ClientEvent | null,
        nowMs: number
    ): void {
        if (!activeEvent || activeEvent.type !== "gravity-well") return;

        const center = this.camera.worldToScreen(activeEvent.x, activeEvent.y);
        const zoom = this.camera.getZoom();
        const screenRadius = activeEvent.radius * zoom;
        const coreScreenRadius = GRAVITY_CORE_RADIUS * zoom;
        const { width, height } = this.camera.getViewport();

        // Culling
        if (
            center.x + screenRadius < 0 ||
            center.x - screenRadius > width ||
            center.y + screenRadius < 0 ||
            center.y - screenRadius > height
        ) {
            return;
        }

        ctx.save();

        // 1. Relleno tenue violeta de fondo
        ctx.fillStyle = "rgba(126, 34, 206, 0.08)";
        ctx.beginPath();
        ctx.arc(center.x, center.y, screenRadius, 0, Math.PI * 2);
        ctx.fill();

        // 2. 4 anillos concéntricos con alpha decreciente desde el centro
        const ringCount = 4;
        for (let k = 1; k <= ringCount; k++) {
            const r = (screenRadius * k) / ringCount;
            const alpha = 0.15 + (1 - k / ringCount) * 0.35;
            ctx.strokeStyle = "#a855f7";
            ctx.lineWidth = Math.max(1, 2 * zoom);
            ctx.globalAlpha = alpha;
            ctx.beginPath();
            ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
            ctx.stroke();
        }

        // 3. Líneas radiales de atracción hacia el centro (16 líneas con rotación y alpha pulsante)
        const lineCount = 16;
        const rotOffset = (nowMs * 0.0006) % (Math.PI * 2);
        for (let i = 0; i < lineCount; i++) {
            const angle = (i / lineCount) * Math.PI * 2 + rotOffset;
            const x1 = center.x + Math.cos(angle) * screenRadius;
            const y1 = center.y + Math.sin(angle) * screenRadius;
            const x2 = center.x + Math.cos(angle) * coreScreenRadius;
            const y2 = center.y + Math.sin(angle) * coreScreenRadius;

            const linePulse = 0.2 + 0.25 * Math.sin(nowMs * 0.005 + i);
            ctx.strokeStyle = "#c084fc";
            ctx.lineWidth = Math.max(1, 1.5 * zoom);
            ctx.globalAlpha = Math.max(0.05, linePulse);
            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();
        }

        // 4. Núcleo (GRAVITY_CORE_RADIUS): círculo violeta brillante
        ctx.globalAlpha = 0.4;
        ctx.fillStyle = "#c084fc";
        ctx.beginPath();
        ctx.arc(center.x, center.y, coreScreenRadius * 1.3, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalAlpha = 0.9;
        ctx.fillStyle = "#7e22ce";
        ctx.beginPath();
        ctx.arc(center.x, center.y, coreScreenRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.strokeStyle = "#f3e8ff";
        ctx.lineWidth = Math.max(1.5, 2.5 * zoom);
        ctx.stroke();

        // 5. Ícono central de vórtice
        ctx.font = `bold ${Math.max(14, Math.round(18 * zoom))}px system-ui, sans-serif`;
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillStyle = "#ffffff";
        ctx.globalAlpha = 1.0;
        ctx.fillText("🌀", center.x, center.y);

        ctx.restore();
    }

    /** Dibuja flashes y ondas expansivas al capturar a un jugador en el pozo gravitatorio */
    private drawGravityCaptures(
        ctx: CanvasRenderingContext2D,
        gravityCaptures: GravityCaptureEffect[],
        nowMs: number
    ): void {
        if (!gravityCaptures || gravityCaptures.length === 0) return;
        const { width, height } = this.camera.getViewport();
        const zoom = this.camera.getZoom();

        for (let i = 0; i < gravityCaptures.length; i++) {
            const effect = gravityCaptures[i];
            const progress = Math.max(0, Math.min(1, (nowMs - effect.startTime) / effect.duration));
            if (progress >= 1) continue;

            const screen = this.camera.worldToScreen(effect.x, effect.y);
            const radius = (40 + progress * 80) * zoom;
            const alpha = 1 - progress;

            // Culling
            if (
                screen.x + radius < 0 ||
                screen.x - radius > width ||
                screen.y + radius < 0 ||
                screen.y - radius > height
            ) {
                continue;
            }

            ctx.save();
            ctx.globalAlpha = alpha;

            // Anillo violeta expansivo
            ctx.strokeStyle = "#c084fc";
            ctx.lineWidth = Math.max(1, (4 * (1 - progress) + 1) * zoom);
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, radius, 0, Math.PI * 2);
            ctx.stroke();

            // Flash central
            const flashRadius = 30 * (1 - progress) * zoom;
            if (flashRadius > 0) {
                ctx.fillStyle = "#f3e8ff";
                ctx.globalAlpha = alpha * 0.75;
                ctx.beginPath();
                ctx.arc(screen.x, screen.y, flashRadius, 0, Math.PI * 2);
                ctx.fill();
            }

            ctx.restore();
        }
    }

    private getMorphologyTier(mass: number): 1 | 2 | 3 | 4 {
        if (mass < 250) return 1;
        if (mass < 800) return 2;
        if (mass < 2000) return 3;
        return 4;
    }

    private drawElementalBody(
        ctx: CanvasRenderingContext2D,
        player: RenderablePlayer,
        sx: number,
        sy: number,
        radius: number,
        zoom: number,
        nowMs: number
    ): void {
        const tier = this.getMorphologyTier(player.mass);
        const speed = Math.hypot(player.vx, player.vy);
        const stretch = Math.min(0.26, speed * 0.0008);
        const seed = hashString(player.sessionId);

        ctx.save();
        ctx.translate(sx, sy);

        switch (player.element) {
            case "water":
                this.drawWaterEntity(ctx, radius, tier, player.facingAngle, stretch, nowMs, seed);
                break;
            case "fire":
                this.drawFireEntity(ctx, radius, tier, player.facingAngle, stretch, nowMs, seed);
                break;
            case "earth":
                this.drawEarthEntity(ctx, radius, tier, nowMs, seed);
                break;
            case "plant":
                this.drawPlantEntity(ctx, radius, tier, nowMs, seed);
                break;
            case "electric":
                this.drawElectricEntity(ctx, radius, tier, nowMs, seed);
                break;
            default: {
                ctx.beginPath();
                ctx.arc(0, 0, radius, 0, Math.PI * 2);
                ctx.fillStyle = ELEMENT_COLORS[player.element] || "#888888";
                ctx.fill();
            }
        }

        ctx.restore();
        void zoom;
    }

    /** AGUA: Ameba líquida con lóbulos pronunciados, brillo especular y gotas/burbujas internas */
    private drawWaterEntity(
        ctx: CanvasRenderingContext2D,
        radius: number,
        tier: 1 | 2 | 3 | 4,
        facingAngle: number,
        stretch: number,
        nowMs: number,
        seed: number
    ): void {
        // Halo acuático exterior
        const outerGlow = ctx.createRadialGradient(0, 0, radius * 0.4, 0, 0, radius * 1.25);
        outerGlow.addColorStop(0, "rgba(56, 189, 248, 0.25)");
        outerGlow.addColorStop(1, "rgba(56, 189, 248, 0)");
        ctx.fillStyle = outerGlow;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 1.25, 0, Math.PI * 2);
        ctx.fill();

        ctx.save();
        ctx.rotate(facingAngle);
        ctx.scale(1 + stretch, 1 - stretch * 0.55);

        const lobes = 7 + tier * 2;
        const pts: Array<{ x: number; y: number }> = [];
        for (let i = 0; i < lobes; i++) {
            const ang = (i / lobes) * Math.PI * 2;
            const wave1 = Math.sin(nowMs * 0.004 + i * 2.1 + (seed % 10)) * (radius * 0.14);
            const wave2 = Math.cos(nowMs * 0.006 - i * 1.3) * (radius * 0.07);
            const r = radius * 0.92 + wave1 + wave2;
            pts.push({ x: Math.cos(ang) * r, y: Math.sin(ang) * r });
        }

        const waterGrad = ctx.createRadialGradient(-radius * 0.2, -radius * 0.2, radius * 0.08, 0, 0, radius * 1.1);
        waterGrad.addColorStop(0, "rgba(186, 230, 253, 0.95)");
        waterGrad.addColorStop(0.55, "rgba(14, 165, 233, 0.88)");
        waterGrad.addColorStop(1, "rgba(3, 105, 161, 0.95)");

        ctx.fillStyle = waterGrad;
        ctx.strokeStyle = "#bae6fd";
        ctx.lineWidth = Math.max(1.5, radius * 0.05);

        ctx.beginPath();
        const startMidX = (pts[0].x + pts[lobes - 1].x) * 0.5;
        const startMidY = (pts[0].y + pts[lobes - 1].y) * 0.5;
        ctx.moveTo(startMidX, startMidY);
        for (let i = 0; i < lobes; i++) {
            const next = pts[(i + 1) % lobes];
            const mx = (pts[i].x + next.x) * 0.5;
            const my = (pts[i].y + next.y) * 0.5;
            ctx.quadraticCurveTo(pts[i].x, pts[i].y, mx, my);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // Brillo líquido interior y burbujas
        const bubbleCount = 2 + tier * 2;
        ctx.fillStyle = "rgba(255, 255, 255, 0.45)";
        for (let b = 0; b < bubbleCount; b++) {
            const bAng = nowMs * 0.0012 * (b % 2 === 0 ? 1 : -1) + b * 1.9;
            const bDist = ((b + 1) / (bubbleCount + 1)) * radius * 0.62;
            const bRad = Math.max(1.5, radius * (0.045 + (b % 3) * 0.025));
            ctx.beginPath();
            ctx.arc(Math.cos(bAng) * bDist, Math.sin(bAng) * bDist, bRad, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    }

    /** FUEGO: Flama viva multicapa con lenguas ascendentes/direccionales y núcleo incandescente */
    private drawFireEntity(
        ctx: CanvasRenderingContext2D,
        radius: number,
        tier: 1 | 2 | 3 | 4,
        facingAngle: number,
        stretch: number,
        nowMs: number,
        seed: number
    ): void {
        ctx.save();
        ctx.globalCompositeOperation = "lighter";

        // Halo térmico rojizo
        const heatAura = ctx.createRadialGradient(0, 0, radius * 0.2, 0, 0, radius * 1.3);
        heatAura.addColorStop(0, "rgba(239, 68, 68, 0.45)");
        heatAura.addColorStop(0.7, "rgba(239, 68, 68, 0.15)");
        heatAura.addColorStop(1, "rgba(239, 68, 68, 0)");
        ctx.fillStyle = heatAura;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 1.3, 0, Math.PI * 2);
        ctx.fill();

        const layers = [
            { scale: 1.05, color: "rgba(220, 38, 38, 0.85)", peaks: 6 + tier * 2, speed: 0.007 },
            { scale: 0.80, color: "rgba(249, 115, 22, 0.90)", peaks: 5 + tier, speed: -0.009 },
            { scale: 0.50, color: "rgba(254, 240, 138, 0.95)", peaks: 4 + tier, speed: 0.012 },
        ];

        const tailAngle = facingAngle + Math.PI;

        for (const layer of layers) {
            ctx.fillStyle = layer.color;
            ctx.beginPath();
            const n = layer.peaks;
            for (let i = 0; i < n; i++) {
                const a1 = (i / n) * Math.PI * 2;
                const a2 = ((i + 1) / n) * Math.PI * 2;
                const aMid = (a1 + a2) * 0.5;

                // Estiramiento hacia la cola de movimiento o hacia arriba en reposo
                const align = stretch > 0.03 ? Math.cos(aMid - tailAngle) : Math.cos(aMid + Math.PI / 2);
                const flameBoost = align > 0 ? align * (stretch > 0.03 ? stretch * 1.6 : 0.22) * radius : 0;

                const flicker = Math.sin(nowMs * layer.speed + i * 1.9 + (seed % 20)) * (radius * 0.13);
                const rTip = radius * layer.scale + flicker + flameBoost;
                const rValley = radius * layer.scale * 0.72;

                const x1 = Math.cos(a1) * rValley;
                const y1 = Math.sin(a1) * rValley;
                const cx = Math.cos(aMid) * rTip;
                const cy = Math.sin(aMid) * rTip;
                const x2 = Math.cos(a2) * rValley;
                const y2 = Math.sin(a2) * rValley;

                if (i === 0) ctx.moveTo(x1, y1);
                ctx.quadraticCurveTo(cx, cy, x2, y2);
            }
            ctx.closePath();
            ctx.fill();
        }

        // Brasas incandescentes orbitando en Tiers 2, 3 y 4
        if (tier >= 2) {
            const embers = tier * 2;
            ctx.fillStyle = "#fef08a";
            for (let e = 0; e < embers; e++) {
                const ang = nowMs * 0.004 + (e * Math.PI * 2) / embers;
                const dist = radius * (0.95 + Math.sin(nowMs * 0.008 + e) * 0.12);
                ctx.beginPath();
                ctx.arc(Math.cos(ang) * dist, Math.sin(ang) * dist, Math.max(1.5, radius * 0.045), 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.restore();
    }

    /** TIERRA: Vórtice de polvo y cráter central rodeado de fragmentos de roca y cristales orbitales */
    private drawEarthEntity(
        ctx: CanvasRenderingContext2D,
        radius: number,
        tier: 1 | 2 | 3 | 4,
        nowMs: number,
        seed: number
    ): void {
        // 1. Nube de polvo sísmico de fondo
        const dustGrad = ctx.createRadialGradient(0, 0, radius * 0.2, 0, 0, radius * 1.18);
        dustGrad.addColorStop(0, "rgba(120, 53, 15, 0.95)");
        dustGrad.addColorStop(0.7, "rgba(146, 64, 14, 0.75)");
        dustGrad.addColorStop(1, "rgba(146, 64, 14, 0)");
        ctx.fillStyle = dustGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 1.18, 0, Math.PI * 2);
        ctx.fill();

        // 2. Cuerpo rocoso facetado principal
        ctx.save();
        ctx.rotate(nowMs * 0.0003);
        const sides = 7 + tier * 2;
        ctx.fillStyle = "#92400e";
        ctx.strokeStyle = "#b45309";
        ctx.lineWidth = Math.max(1.5, radius * 0.05);
        ctx.beginPath();
        for (let i = 0; i < sides; i++) {
            const ang = (i / sides) * Math.PI * 2;
            const noise = Math.sin(seed + i * 7.13) * 0.12;
            const r = radius * (0.82 + noise);
            const px = Math.cos(ang) * r;
            const py = Math.sin(ang) * r;
            if (i === 0) ctx.moveTo(px, py);
            else ctx.lineTo(px, py);
        }
        ctx.closePath();
        ctx.fill();
        ctx.stroke();

        // 3. Cráter / Núcleo gravitacional oscuro en el centro
        const craterGrad = ctx.createRadialGradient(0, 0, radius * 0.05, 0, 0, radius * 0.45);
        craterGrad.addColorStop(0, "#271206");
        craterGrad.addColorStop(1, "#451a03");
        ctx.fillStyle = craterGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.44, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();

        // 4. Anillo de fragmentos de roca y cristales girando alrededor del cráter
        const rockCount = 5 + tier * 3;
        for (let rIdx = 0; rIdx < rockCount; rIdx++) {
            const dir = rIdx % 2 === 0 ? 1 : -1;
            const orbitSpeed = 0.0008 * dir;
            const ang = (rIdx / rockCount) * Math.PI * 2 + nowMs * orbitSpeed;
            const dist = radius * (0.68 + ((rIdx * 19) % 30) * 0.01);
            const rx = Math.cos(ang) * dist;
            const ry = Math.sin(ang) * dist;
            const rockSize = Math.max(2.5, radius * (0.10 + (rIdx % 3) * 0.035));

            ctx.save();
            ctx.translate(rx, ry);
            ctx.rotate(ang + rIdx);

            // En Tiers 2+, intercalar cristales brillantes de cuarzo/ámbar
            if (tier >= 2 && rIdx % 4 === 0) {
                ctx.fillStyle = "#fde68a";
                ctx.strokeStyle = "#f59e0b";
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(0, -rockSize * 1.3);
                ctx.lineTo(rockSize * 0.7, 0);
                ctx.lineTo(0, rockSize * 1.3);
                ctx.lineTo(-rockSize * 0.7, 0);
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
            } else {
                // Fragmento de roca angular
                ctx.fillStyle = rIdx % 2 === 0 ? "#b45309" : "#78350f";
                ctx.strokeStyle = "#d97706";
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(-rockSize, -rockSize * 0.5);
                ctx.lineTo(0, -rockSize);
                ctx.lineTo(rockSize, -rockSize * 0.3);
                ctx.lineTo(rockSize * 0.6, rockSize * 0.9);
                ctx.lineTo(-rockSize * 0.7, rockSize * 0.7);
                ctx.closePath();
                ctx.fill();
                ctx.stroke();
            }
            ctx.restore();
        }
    }

    /** PLANTA: Arbusto/Biomasa frondosa multicapa con hojas superpuestas y esporas bioluminiscentes */
    private drawPlantEntity(
        ctx: CanvasRenderingContext2D,
        radius: number,
        tier: 1 | 2 | 3 | 4,
        nowMs: number,
        seed: number
    ): void {
        const breathe = 1 + Math.sin(nowMs * 0.003 + (seed % 15)) * 0.035;
        ctx.save();
        ctx.scale(breathe, breathe);

        // 1. Halo de esporas verdes
        const bioAura = ctx.createRadialGradient(0, 0, radius * 0.3, 0, 0, radius * 1.22);
        bioAura.addColorStop(0, "rgba(34, 197, 94, 0.3)");
        bioAura.addColorStop(1, "rgba(34, 197, 94, 0)");
        ctx.fillStyle = bioAura;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 1.22, 0, Math.PI * 2);
        ctx.fill();

        // 2. Corona exterior de hojas oscuras
        const outerLeaves = 8 + tier * 2;
        for (let i = 0; i < outerLeaves; i++) {
            const ang = (i / outerLeaves) * Math.PI * 2 + Math.sin(nowMs * 0.002 + i) * 0.06;
            ctx.save();
            ctx.rotate(ang);
            ctx.fillStyle = i % 2 === 0 ? "#15803d" : "#166534";
            ctx.strokeStyle = "#22c55e";
            ctx.lineWidth = 1.2;
            ctx.beginPath();
            ctx.moveTo(radius * 0.25, 0);
            ctx.quadraticCurveTo(radius * 0.75, -radius * 0.32, radius * 1.06, 0);
            ctx.quadraticCurveTo(radius * 0.75, radius * 0.32, radius * 0.25, 0);
            ctx.fill();
            ctx.stroke();
            ctx.restore();
        }

        // 3. Corona intermedia de hojas esmeralda brillantes
        const innerLeaves = 6 + tier;
        for (let j = 0; j < innerLeaves; j++) {
            const ang = (j / innerLeaves) * Math.PI * 2 + 0.3 - Math.cos(nowMs * 0.0025 + j) * 0.05;
            ctx.save();
            ctx.rotate(ang);
            ctx.fillStyle = "#22c55e";
            ctx.beginPath();
            ctx.moveTo(radius * 0.15, 0);
            ctx.quadraticCurveTo(radius * 0.55, -radius * 0.24, radius * 0.82, 0);
            ctx.quadraticCurveTo(radius * 0.55, radius * 0.24, radius * 0.15, 0);
            ctx.fill();
            ctx.restore();
        }

        // 4. Núcleo bioluminiscente de clorofila
        const coreGrad = ctx.createRadialGradient(0, 0, radius * 0.05, 0, 0, radius * 0.55);
        coreGrad.addColorStop(0, "#dcfce7");
        coreGrad.addColorStop(0.5, "#86efac");
        coreGrad.addColorStop(1, "rgba(22, 163, 74, 0)");
        ctx.fillStyle = coreGrad;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 0.55, 0, Math.PI * 2);
        ctx.fill();

        // 5. Esporas luminosas flotando alrededor
        const sporeCount = 3 + tier * 2;
        ctx.fillStyle = "#fef08a";
        for (let s = 0; s < sporeCount; s++) {
            const sAng = nowMs * 0.001 + (s * Math.PI * 2) / sporeCount;
            const sDist = radius * (0.55 + Math.sin(nowMs * 0.004 + s * 2) * 0.38);
            ctx.beginPath();
            ctx.arc(Math.cos(sAng) * sDist, Math.sin(sAng) * sDist, Math.max(1.5, radius * 0.045), 0, Math.PI * 2);
            ctx.fill();
        }

        ctx.restore();
    }

    /** ELECTRICIDAD: Sol/Núcleo voltaico hiperactivo con rayos fractales ramificados */
    private drawElectricEntity(
        ctx: CanvasRenderingContext2D,
        radius: number,
        tier: 1 | 2 | 3 | 4,
        nowMs: number,
        seed: number
    ): void {
        ctx.save();
        ctx.globalCompositeOperation = "lighter";

        // 1. Resplandor de plasma amarillo/blanco central
        const plasma = ctx.createRadialGradient(0, 0, radius * 0.05, 0, 0, radius * 1.15);
        plasma.addColorStop(0, "rgba(255, 255, 255, 1)");
        plasma.addColorStop(0.28, "rgba(254, 240, 138, 0.85)");
        plasma.addColorStop(0.65, "rgba(234, 179, 8, 0.35)");
        plasma.addColorStop(1, "rgba(234, 179, 8, 0)");
        ctx.fillStyle = plasma;
        ctx.beginPath();
        ctx.arc(0, 0, radius * 1.15, 0, Math.PI * 2);
        ctx.fill();

        // 2. Rayos fractales principales y ramificaciones secundarias
        const boltCount = 7 + tier * 3;
        const tick = Math.floor(nowMs / 30);

        for (let b = 0; b < boltCount; b++) {
            const baseAng = (b / boltCount) * Math.PI * 2 + ((tick * 13 + b * 7) % 100) * 0.015;
            const segments = 5;

            ctx.strokeStyle = b % 2 === 0 ? "#ffffff" : "#facc15";
            ctx.lineWidth = b % 2 === 0 ? Math.max(1.5, radius * 0.055) : Math.max(1, radius * 0.035);
            ctx.beginPath();
            ctx.moveTo(0, 0);

            let prevX = 0;
            let prevY = 0;
            for (let s = 1; s <= segments; s++) {
                const frac = s / segments;
                const rand = Math.sin(seed + b * 43 + s * 19 + tick * 29);
                const jitterAng = baseAng + rand * (0.28 * (1 - frac * 0.3));
                const reach = radius * frac * (s === segments ? 1.18 : 0.96);
                const nx = Math.cos(jitterAng) * reach;
                const ny = Math.sin(jitterAng) * reach;
                ctx.lineTo(nx, ny);

                // Ramificación fractal a mitad de camino
                if (s === 3 && tier >= 2 && b % 2 === 0) {
                    const branchAng = jitterAng + (rand > 0 ? 0.45 : -0.45);
                    const branchReach = reach * 1.35;
                    ctx.moveTo(nx, ny);
                    ctx.lineTo(Math.cos(branchAng) * branchReach, Math.sin(branchAng) * branchReach);
                    ctx.moveTo(nx, ny);
                }
                prevX = nx;
                prevY = ny;
            }
            void prevX;
            void prevY;
            ctx.stroke();
        }

        ctx.restore();
    }

    /** Dibuja partículas de polvo alquímico ambiental con parallax y resplandor bajo titanes */
    private drawAmbientParallax(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        nowMs: number,
        players: RenderablePlayer[]
    ): void {
        const zoom = this.camera.getZoom();

        // 1. Resplandor radial suave bajo titanes (mass >= 800)
        for (let i = 0; i < players.length; i++) {
            const p = players[i];
            if (!p.alive || p.mass < 800) continue;
            const screen = this.camera.worldToScreen(p.renderX, p.renderY);
            const glowRadius = Math.sqrt(p.mass) * 3.2 * zoom;

            // Culling de pantalla
            if (
                screen.x + glowRadius < 0 ||
                screen.x - glowRadius > width ||
                screen.y + glowRadius < 0 ||
                screen.y - glowRadius > height
            ) {
                continue;
            }

            const color = ELEMENT_COLORS[p.element] || "#38bdf8";
            const glow = ctx.createRadialGradient(screen.x, screen.y, 0, screen.x, screen.y, glowRadius);
            glow.addColorStop(0, color);
            glow.addColorStop(1, "transparent");

            ctx.save();
            ctx.globalAlpha = 0.07;
            ctx.fillStyle = glow;
            ctx.beginPath();
            ctx.arc(screen.x, screen.y, glowRadius, 0, Math.PI * 2);
            ctx.fill();
            ctx.restore();
        }

        // 2. 45 motas de energía ambiental con coordenadas deterministas y parallax
        const camCenter = this.camera.screenToWorld(width / 2, height / 2);
        const motesCount = 45;
        const colors = ["#38bdf8", "#a78bfa"];

        ctx.save();
        for (let m = 0; m < motesCount; m++) {
            const layer = m % 2 === 0 ? 0.15 : 0.35;
            const baseX = ((m * 179 + 37) % 3000);
            const baseY = ((m * 223 + 71) % 3000);

            const screenX = ((baseX - camCenter.x * layer + width * 10) % width);
            const screenY = ((baseY - camCenter.y * layer + height * 10) % height);

            const pulse = Math.sin(nowMs * 0.002 + m * 1.7);
            const alpha = 0.08 + (0.5 + 0.5 * pulse) * 0.14;
            const radius = 1 + (m % 3) * 0.6;

            ctx.globalAlpha = alpha;
            ctx.fillStyle = colors[m % colors.length];
            ctx.beginPath();
            ctx.arc(screenX, screenY, radius, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();
    }

    /** Dibuja estelas de movimiento temáticas según el elemento del jugador */
    private drawElementalTrails(
        ctx: CanvasRenderingContext2D,
        trails: Map<string, TrailPoint[]>,
        width: number,
        height: number,
        nowMs: number
    ): void {
        const zoom = this.camera.getZoom();

        for (const [, trail] of trails) {
            if (trail.length < 2) continue;

            for (let i = 1; i < trail.length; i++) {
                const p0 = trail[i - 1];
                const p1 = trail[i];
                const age = (nowMs - p1.time) / 200; // 0 (nuevo) a 1 (expirado)
                if (age >= 1 || age < 0) continue;

                const screen0 = this.camera.worldToScreen(p0.x, p0.y);
                const screen1 = this.camera.worldToScreen(p1.x, p1.y);

                // Culling de pantalla
                if (
                    (screen1.x < -50 && screen0.x < -50) ||
                    (screen1.x > width + 50 && screen0.x > width + 50) ||
                    (screen1.y < -50 && screen0.y < -50) ||
                    (screen1.y > height + 50 && screen0.y > height + 50)
                ) {
                    continue;
                }

                const alpha = (1 - age) * 0.45;
                const r = Math.max(1.5, p1.radius * (1 - age) * 0.38 * zoom);
                const seed = p1.seed;

                ctx.save();

                switch (p1.element) {
                    case "water": {
                        const offsetX = (seed - 0.5) * r * 0.8;
                        const bx = screen1.x + offsetX;
                        const by = screen1.y;

                        ctx.beginPath();
                        ctx.arc(bx, by, r, 0, Math.PI * 2);
                        ctx.fillStyle = "#38bdf8";
                        ctx.globalAlpha = alpha * 0.55;
                        ctx.fill();

                        ctx.strokeStyle = "#bae6fd";
                        ctx.lineWidth = Math.max(1, 1.2 * zoom);
                        ctx.globalAlpha = alpha * 0.8;
                        ctx.stroke();
                        break;
                    }
                    case "fire": {
                        const fireColor = age > 0.5
                            ? "#78716c"
                            : (i % 2 === 0 ? "#f97316" : "#ef4444");
                        const liftY = screen1.y - age * 14 * zoom;
                        const emberR = Math.max(1.2, r * (age > 0.5 ? 0.9 : 0.6));

                        ctx.beginPath();
                        ctx.arc(screen1.x + (seed - 0.5) * 6 * zoom, liftY, emberR, 0, Math.PI * 2);
                        ctx.fillStyle = fireColor;
                        ctx.globalAlpha = alpha;
                        ctx.fill();
                        break;
                    }
                    case "earth": {
                        const rockColor = i % 2 === 0 ? "#b45309" : "#78350f";
                        ctx.translate(screen1.x, screen1.y);
                        ctx.rotate(seed * Math.PI * 2);
                        ctx.fillStyle = rockColor;
                        ctx.globalAlpha = alpha * 0.7;
                        ctx.beginPath();
                        ctx.moveTo(0, -r);
                        ctx.lineTo(r * 0.8, 0);
                        ctx.lineTo(0, r);
                        ctx.lineTo(-r * 0.8, 0);
                        ctx.closePath();
                        ctx.fill();
                        break;
                    }
                    case "plant": {
                        if (i % 3 === 0) {
                            ctx.beginPath();
                            ctx.arc(screen1.x, screen1.y, Math.max(1.5, r * 0.45), 0, Math.PI * 2);
                            ctx.fillStyle = "#fde047";
                            ctx.globalAlpha = alpha * 0.9;
                            ctx.fill();
                        } else {
                            const leafAngle = Math.atan2(screen1.y - screen0.y, screen1.x - screen0.x) + (seed - 0.5) * 0.6;
                            ctx.beginPath();
                            ctx.ellipse(screen1.x, screen1.y, r, Math.max(1, r * 0.45), leafAngle, 0, Math.PI * 2);
                            ctx.fillStyle = "#22c55e";
                            ctx.globalAlpha = alpha;
                            ctx.fill();
                        }
                        break;
                    }
                    case "electric": {
                        const dx = screen1.x - screen0.x;
                        const dy = screen1.y - screen0.y;
                        const dist = Math.hypot(dx, dy);
                        const perpX = dist > 0.001 ? (-dy / dist) : 0;
                        const perpY = dist > 0.001 ? (dx / dist) : 0;
                        const midX = (screen0.x + screen1.x) * 0.5 + perpX * (seed - 0.5) * 14 * zoom;
                        const midY = (screen0.y + screen1.y) * 0.5 + perpY * (seed - 0.5) * 14 * zoom;

                        ctx.strokeStyle = i % 2 === 0 ? "#facc15" : "#ffffff";
                        ctx.lineWidth = Math.max(1, 1.8 * zoom);
                        ctx.globalAlpha = alpha;
                        ctx.beginPath();
                        ctx.moveTo(screen0.x, screen0.y);
                        ctx.lineTo(midX, midY);
                        ctx.lineTo(screen1.x, screen1.y);
                        ctx.stroke();
                        break;
                    }
                    default: {
                        ctx.strokeStyle = ELEMENT_COLORS[p1.element] || "#888888";
                        ctx.lineWidth = r;
                        ctx.globalAlpha = alpha * 0.5;
                        ctx.beginPath();
                        ctx.moveTo(screen0.x, screen0.y);
                        ctx.lineTo(screen1.x, screen1.y);
                        ctx.stroke();
                        break;
                    }
                }

                ctx.restore();
            }
        }
    }

    /** Calcula los límites actuales del viewport en coordenadas del mundo. */
    private getViewportBounds(): { left: number; top: number; right: number; bottom: number } {
        const { width, height } = this.camera.getViewport();
        const topLeft = this.camera.screenToWorld(0, 0);
        const bottomRight = this.camera.screenToWorld(width, height);
        return {
            left: topLeft.x,
            top: topLeft.y,
            right: bottomRight.x,
            bottom: bottomRight.y,
        };
    }

    /** Evalúa proximidad entre jugadores vivos y dibuja reacciones elementales (vapor, arcos voltaicos, raíces, chispas) */
    private processAndDrawProximityReactions(
        ctx: CanvasRenderingContext2D,
        players: RenderablePlayer[],
        width: number,
        height: number,
        nowMs: number
    ): void {
        const zoom = this.camera.getZoom();
        const canSpawn = nowMs - this.lastProximitySpawnAt >= 45;
        let spawnedThisFrame = false;

        for (let i = 0; i < players.length; i++) {
            const pA = players[i];
            if (!pA.alive) continue;
            const rA = Math.sqrt(pA.mass) * 2;

            for (let j = i + 1; j < players.length; j++) {
                const pB = players[j];
                if (!pB.alive) continue;
                if (pA.element === pB.element) continue;
                const rB = Math.sqrt(pB.mass) * 2;

                const dx = pB.renderX - pA.renderX;
                const dy = pB.renderY - pA.renderY;
                const centerDist = Math.hypot(dx, dy);
                const edgeGap = centerDist - (rA + rB);

                if (edgeGap > 55 || centerDist < 1) continue;

                const nx = dx / centerDist;
                const ny = dy / centerDist;
                const midWorldX = pA.renderX + nx * (rA + edgeGap * 0.5);
                const midWorldY = pA.renderY + ny * (rA + edgeGap * 0.5);

                const screenMid = this.camera.worldToScreen(midWorldX, midWorldY);
                if (
                    screenMid.x < -60 || screenMid.x > width + 60 ||
                    screenMid.y < -60 || screenMid.y > height + 60
                ) {
                    continue;
                }

                const pair = `${pA.element}+${pB.element}`;

                // 1. Electricidad + Agua: Arco voltaico en vivo conectando ambos bordes
                if (pair === "electric+water" || pair === "water+electric") {
                    const edgeA = this.camera.worldToScreen(pA.renderX + nx * rA, pA.renderY + ny * rA);
                    const edgeB = this.camera.worldToScreen(pB.renderX - nx * rB, pB.renderY - ny * rB);
                    const perpX = -ny * Math.sin(nowMs * 0.09 + i) * 10 * zoom;
                    const perpY = nx * Math.sin(nowMs * 0.09 + i) * 10 * zoom;

                    ctx.save();
                    ctx.strokeStyle = "#fef08a";
                    ctx.lineWidth = Math.max(1.5, 2.2 * zoom);
                    ctx.beginPath();
                    ctx.moveTo(edgeA.x, edgeA.y);
                    ctx.lineTo(screenMid.x + perpX, screenMid.y + perpY);
                    ctx.lineTo(edgeB.x, edgeB.y);
                    ctx.stroke();
                    ctx.restore();
                }

                // 2. Fuego + Agua: Cortina de vapor blanco ascendente
                if (canSpawn && (pair === "fire+water" || pair === "water+fire")) {
                    this.spawnProximityParticle(midWorldX, midWorldY, "steam", nowMs);
                    spawnedThisFrame = true;
                }

                // 3. Tierra + Planta: Brotación de raíces/esporas esmeralda
                if (canSpawn && (pair === "earth+plant" || pair === "plant+earth")) {
                    this.spawnProximityParticle(midWorldX, midWorldY, "root-bloom", nowMs);
                    spawnedThisFrame = true;
                }

                // 4. Fuego + Planta: Chispas de combustión
                if (canSpawn && (pair === "fire+plant" || pair === "plant+fire")) {
                    this.spawnProximityParticle(midWorldX, midWorldY, "ember-spark", nowMs);
                    spawnedThisFrame = true;
                }
            }
        }

        if (spawnedThisFrame) {
            this.lastProximitySpawnAt = nowMs;
        }

        // Actualizar y renderizar partículas reactivas
        for (let k = this.proximityParticles.length - 1; k >= 0; k--) {
            const pt = this.proximityParticles[k];
            const elapsed = nowMs - pt.birthTime;
            if (elapsed >= pt.duration) {
                this.proximityParticles.splice(k, 1);
                continue;
            }
            const t = elapsed / pt.duration;
            const wx = pt.x + pt.vx * (elapsed * 0.001);
            const wy = pt.y + pt.vy * (elapsed * 0.001);
            const scr = this.camera.worldToScreen(wx, wy);

            ctx.save();
            if (pt.kind === "steam") {
                ctx.fillStyle = `rgba(226, 232, 240, ${(1 - t) * 0.5})`;
                ctx.beginPath();
                ctx.arc(scr.x, scr.y, pt.radius * (1 + t * 1.3) * zoom, 0, Math.PI * 2);
                ctx.fill();
            } else if (pt.kind === "root-bloom") {
                ctx.fillStyle = `rgba(74, 222, 128, ${(1 - t) * 0.85})`;
                ctx.beginPath();
                ctx.arc(scr.x, scr.y, pt.radius * Math.sin(t * Math.PI) * zoom, 0, Math.PI * 2);
                ctx.fill();
            } else if (pt.kind === "ember-spark") {
                ctx.fillStyle = t < 0.4 ? "#fef08a" : "#f97316";
                ctx.globalAlpha = 1 - t;
                ctx.beginPath();
                ctx.arc(scr.x, scr.y, Math.max(1.5, pt.radius * (1 - t) * zoom), 0, Math.PI * 2);
                ctx.fill();
            }
            ctx.restore();
        }
    }

    private spawnProximityParticle(x: number, y: number, kind: ProximityReactionKind, nowMs: number): void {
        if (this.proximityParticles.length >= 100) {
            this.proximityParticles.shift();
        }
        const angle = Math.random() * Math.PI * 2;
        const speed = kind === "steam" ? 20 + Math.random() * 25 : 15 + Math.random() * 25;
        this.proximityParticles.push({
            x: x + (Math.random() - 0.5) * 18,
            y: y + (Math.random() - 0.5) * 18,
            vx: Math.cos(angle) * speed,
            vy: kind === "steam" ? -Math.abs(Math.sin(angle) * speed) - 18 : Math.sin(angle) * speed,
            radius: kind === "steam" ? 5 + Math.random() * 5 : 3 + Math.random() * 3,
            kind,
            birthTime: nowMs,
            duration: kind === "steam" ? 600 : 450,
        });
    }

    /** Dibuja geometría especializada adicional en los impactos de counter elemental */
    private drawSpecializedElementalBurst(
        ctx: CanvasRenderingContext2D,
        sx: number,
        sy: number,
        effectType: string,
        progress: number,
        color: string,
        seedTime: number
    ): void {
        const zoom = this.camera.getZoom();
        ctx.save();
        ctx.globalAlpha = Math.max(0, 1 - progress);

        if (effectType === "electric->water" || effectType === "chain-electric") {
            // Relámpagos radiales quebrados
            ctx.strokeStyle = "#fef08a";
            ctx.lineWidth = Math.max(1.5, 2.5 * (1 - progress) * zoom);
            for (let b = 0; b < 5; b++) {
                const ang = (b / 5) * Math.PI * 2 + seedTime * 0.01;
                const rMid = (18 + progress * 25) * zoom;
                const rEnd = (32 + progress * 55) * zoom;
                ctx.beginPath();
                ctx.moveTo(sx, sy);
                ctx.lineTo(sx + Math.cos(ang + 0.18) * rMid, sy + Math.sin(ang + 0.18) * rMid);
                ctx.lineTo(sx + Math.cos(ang - 0.12) * rEnd, sy + Math.sin(ang - 0.12) * rEnd);
                ctx.stroke();
            }
        } else if (effectType === "water->fire") {
            // Salpicadura hidrodinámica + nubes de vapor blanco
            ctx.fillStyle = "rgba(226, 232, 240, 0.65)";
            for (let v = 0; v < 5; v++) {
                const ang = (v / 5) * Math.PI * 2 - progress * 1.2;
                const dist = (12 + progress * 42) * zoom;
                ctx.beginPath();
                ctx.arc(sx + Math.cos(ang) * dist, sy + Math.sin(ang) * dist - progress * 15 * zoom, (6 + progress * 8) * zoom, 0, Math.PI * 2);
                ctx.fill();
            }
        } else if (effectType === "earth->electric") {
            // Esquirlas de roca y onda sísmica doble
            ctx.strokeStyle = "#fde68a";
            ctx.lineWidth = 2 * (1 - progress);
            ctx.beginPath();
            ctx.arc(sx, sy, (12 + progress * 55) * zoom, 0, Math.PI * 2);
            ctx.stroke();
        } else {
            // Destello alquímico interior para fire->plant, plant->earth y shield-break
            ctx.strokeStyle = color;
            ctx.lineWidth = 2 * (1 - progress);
            ctx.beginPath();
            ctx.arc(sx, sy, (10 + progress * 28) * zoom, 0, Math.PI * 2);
            ctx.stroke();
        }

        ctx.restore();
    }

    /**
     * Dibuja 3 grandes nebulosas cósmicas y el Gran Círculo de Transmutación Alquímica en el centro del suelo.
     */
    private drawAlchemyArenaFloor(
        ctx: CanvasRenderingContext2D,
        width: number,
        height: number,
        nowMs: number,
        isAttractMode: boolean = false
    ): void {
        const zoom = this.camera.getZoom();

        // 1. Nebulosas cósmicas de fondo en coordenadas del mundo (opacidad aumentada en attract mode)
        const nebAlpha0 = isAttractMode ? 0.38 : 0.10;
        const nebAlphaMid = isAttractMode ? 0.14 : 0.04;

        const nebulas = [
            {
                x: 750,
                y: 750,
                color0: `rgba(2, 132, 199, ${nebAlpha0})`,
                colorMid: `rgba(2, 132, 199, ${nebAlphaMid})`,
                color1: "rgba(2, 132, 199, 0)",
            },
            {
                x: 2250,
                y: 750,
                color0: `rgba(126, 34, 206, ${nebAlpha0})`,
                colorMid: `rgba(126, 34, 206, ${nebAlphaMid})`,
                color1: "rgba(126, 34, 206, 0)",
            },
            {
                x: 1500,
                y: 2250,
                color0: `rgba(5, 150, 105, ${nebAlpha0})`,
                colorMid: `rgba(5, 150, 105, ${nebAlphaMid})`,
                color1: "rgba(5, 150, 105, 0)",
            },
        ];
        const nebRadius = 1200 * zoom;

        ctx.save();
        for (let i = 0; i < nebulas.length; i++) {
            const neb = nebulas[i];
            const scr = this.camera.worldToScreen(neb.x, neb.y);
            if (
                scr.x + nebRadius < 0 || scr.x - nebRadius > width ||
                scr.y + nebRadius < 0 || scr.y - nebRadius > height
            ) {
                continue;
            }

            const grad = ctx.createRadialGradient(scr.x, scr.y, 0, scr.x, scr.y, nebRadius);
            grad.addColorStop(0, neb.color0);
            grad.addColorStop(0.5, neb.colorMid);
            grad.addColorStop(1, neb.color1);

            ctx.globalAlpha = 1.0;
            ctx.fillStyle = grad;
            ctx.beginPath();
            ctx.arc(scr.x, scr.y, nebRadius, 0, Math.PI * 2);
            ctx.fill();
        }

        // 2. Gran Círculo de Transmutación Alquímica en el centro
        const centerWorldX = this.mapWidth / 2;
        const centerWorldY = this.mapHeight / 2;
        const centerScreen = this.camera.worldToScreen(centerWorldX, centerWorldY);
        const circleRadius = 420 * zoom;

        if (
            centerScreen.x + circleRadius >= 0 && centerScreen.x - circleRadius <= width &&
            centerScreen.y + circleRadius >= 0 && centerScreen.y - circleRadius <= height
        ) {
            ctx.save();
            ctx.translate(centerScreen.x, centerScreen.y);

            // Dos anillos concéntricos exteriores
            ctx.lineWidth = Math.max(1, 1.8 * zoom);
            ctx.strokeStyle = isAttractMode ? "rgba(56, 189, 248, 0.18)" : "rgba(56, 189, 248, 0.09)";
            ctx.beginPath();
            ctx.arc(0, 0, circleRadius, 0, Math.PI * 2);
            ctx.stroke();

            ctx.strokeStyle = isAttractMode ? "rgba(168, 85, 247, 0.16)" : "rgba(168, 85, 247, 0.08)";
            ctx.beginPath();
            ctx.arc(0, 0, 390 * zoom, 0, Math.PI * 2);
            ctx.stroke();

            // Hexágono inscrito (2 triángulos equiláteros formando un sello alquímico de 6 puntas) rotando
            ctx.save();
            ctx.rotate(nowMs * 0.00015);
            const triRadius = 380 * zoom;
            ctx.strokeStyle = isAttractMode ? "rgba(56, 189, 248, 0.18)" : "rgba(56, 189, 248, 0.09)";
            for (let t = 0; t < 2; t++) {
                const angleOffset = t * Math.PI;
                ctx.beginPath();
                for (let k = 0; k < 3; k++) {
                    const ang = angleOffset + (k * 2 * Math.PI) / 3;
                    const px = Math.cos(ang) * triRadius;
                    const py = Math.sin(ang) * triRadius;
                    if (k === 0) ctx.moveTo(px, py);
                    else ctx.lineTo(px, py);
                }
                ctx.closePath();
                ctx.stroke();
            }
            ctx.restore();

            // Cuadrado inscrito en contra-rotación
            ctx.save();
            ctx.rotate(-nowMs * 0.0001);
            const squareRadius = 260 * zoom;
            ctx.strokeStyle = isAttractMode ? "rgba(168, 85, 247, 0.16)" : "rgba(168, 85, 247, 0.08)";
            ctx.beginPath();
            for (let k = 0; k < 4; k++) {
                const ang = (k * Math.PI) / 2 + Math.PI / 4;
                const px = Math.cos(ang) * squareRadius;
                const py = Math.sin(ang) * squareRadius;
                if (k === 0) ctx.moveTo(px, py);
                else ctx.lineTo(px, py);
            }
            ctx.closePath();
            ctx.stroke();
            ctx.restore();

            // Círculo central
            ctx.strokeStyle = isAttractMode ? "rgba(56, 189, 248, 0.18)" : "rgba(56, 189, 248, 0.09)";
            ctx.beginPath();
            ctx.arc(0, 0, 140 * zoom, 0, Math.PI * 2);
            ctx.stroke();

            ctx.restore();
        }

        ctx.restore();
    }

    /**
     * Dibuja el radar minimapa alquímico semitransparente con jugadores, líder y zonas de evento.
     */
    private drawMinimap(
        ctx: CanvasRenderingContext2D,
        players: RenderablePlayer[],
        localSessionId: string | null,
        activeEvent: ClientEvent | null,
        width: number,
        height: number,
        nowMs: number
    ): void {
        const mobileControlsEl = document.getElementById("mobile-controls");
        const touchControlsVisible = Boolean(mobileControlsEl && !mobileControlsEl.classList.contains("hidden"));

        let mapSize: number;
        let mapX: number;
        let mapY: number;

        const isPrivateHudVisible = document.body.classList.contains("is-private-room");
        if (touchControlsVisible) {
            mapSize = height <= 560 ? 64 : 76;
            mapX = 10;
            mapY = isPrivateHudVisible
                ? (height <= 560 ? 74 : 92)
                : (height <= 560 ? 46 : 58);
        } else {
            mapSize = 116;
            mapX = width - mapSize - 18;
            mapY = height - mapSize - 18;
        }

        const x = mapX;
        const y = mapY;
        const cornerRadius = 8;

        ctx.save();

        // Fondo del minimapa
        ctx.fillStyle = "rgba(11, 14, 20, 0.72)";
        ctx.strokeStyle = "rgba(56, 189, 248, 0.3)";
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        if (typeof ctx.roundRect === "function") {
            ctx.roundRect(x, y, mapSize, mapSize, cornerRadius);
        } else {
            ctx.rect(x, y, mapSize, mapSize);
        }
        ctx.fill();
        ctx.stroke();

        // Clip dentro del minimapa redondeado para que nada sobresalga
        ctx.clip();

        // Cruz central tenue
        ctx.strokeStyle = "rgba(255, 255, 255, 0.08)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + mapSize / 2, y);
        ctx.lineTo(x + mapSize / 2, y + mapSize);
        ctx.moveTo(x, y + mapSize / 2);
        ctx.lineTo(x + mapSize, y + mapSize / 2);
        ctx.stroke();

        // Zona del evento activo escalada
        if (activeEvent) {
            const evX = x + (activeEvent.x / this.mapWidth) * mapSize;
            const evY = y + (activeEvent.y / this.mapHeight) * mapSize;
            const evR = Math.max(3, (activeEvent.radius / this.mapWidth) * mapSize);
            const pulse = 0.5 + 0.5 * Math.sin(nowMs * 0.005);
            ctx.fillStyle = activeEvent.color;
            ctx.globalAlpha = 0.18 + pulse * 0.12;
            ctx.beginPath();
            ctx.arc(evX, evY, evR, 0, Math.PI * 2);
            ctx.fill();
            ctx.strokeStyle = activeEvent.color;
            ctx.globalAlpha = 0.55 + pulse * 0.25;
            ctx.lineWidth = 1;
            ctx.stroke();
            ctx.globalAlpha = 1.0;
        }

        // Buscar Top #1 de masa entre vivos
        let top1SessionId: string | null = null;
        let maxMass = -1;
        for (let i = 0; i < players.length; i++) {
            const p = players[i];
            if (p.alive && p.mass > maxMass) {
                maxMass = p.mass;
                top1SessionId = p.sessionId;
            }
        }

        // Renderizar jugadores en minimapa
        for (let i = 0; i < players.length; i++) {
            const p = players[i];
            if (!p.alive) continue;

            const px = x + (p.renderX / this.mapWidth) * mapSize;
            const py = y + (p.renderY / this.mapHeight) * mapSize;
            const isLocal = p.sessionId === localSessionId;
            const isTop1 = p.sessionId === top1SessionId && !isLocal;

            if (isTop1) {
                // Aro dorado Top #1
                ctx.strokeStyle = "#fbbf24";
                ctx.lineWidth = 1.2;
                ctx.beginPath();
                ctx.arc(px, py, 3.5, 0, Math.PI * 2);
                ctx.stroke();
            }

            if (isLocal) {
                // Halo cian pulsante
                const haloR = 4.5 + Math.sin(nowMs * 0.008) * 1.5;
                ctx.fillStyle = "rgba(56, 189, 248, 0.4)";
                ctx.beginPath();
                ctx.arc(px, py, haloR, 0, Math.PI * 2);
                ctx.fill();

                // Punto blanco brillante local
                ctx.fillStyle = "#ffffff";
                ctx.beginPath();
                ctx.arc(px, py, 3.5, 0, Math.PI * 2);
                ctx.fill();
            } else {
                // Punto normal con color de elemento
                ctx.fillStyle = ELEMENT_COLORS[p.element] || "#94a3b8";
                ctx.beginPath();
                ctx.arc(px, py, 2, 0, Math.PI * 2);
                ctx.fill();
            }
        }

        ctx.restore();
    }
}
